<template>
  <div class="PostComposer column fullWidth" @click.stop>
    <div class="previewArea" v-if="showPreviewArea">
      <!-- Masonry Mockup Container (only in preview mode) -->
      <div v-if="viewMode === 'preview'" key="preview" class="masonry-mockup-wrapper">
        <div
          v-if="usesAdaptivePresentationPreview"
          class="presentation-preview-container"
        >
          <div class="presentation-preview-heading">
            <span>Final preview</span>
            <span class="presentation-preview-mode">
              {{ previewPresentationLabel }}
            </span>
          </div>
          <div class="presentation-preview-phone">
            <PostPresentationPreview :item="presentationPreviewItem" />
          </div>
        </div>
        <div v-else class="masonry-mockup-container">
          <div class="masonry-mockup">
            <!-- Left column - partially visible placeholder cards -->
            <div class="mockup-column mockup-column-edge">
              <div class="mockup-placeholder mockup-placeholder-tall"></div>
              <div class="mockup-placeholder mockup-placeholder-medium"></div>
            </div>
            <!-- Center column - the actual preview using real ImageCard -->
            <div class="mockup-column mockup-column-center">
              <div class="mockup-placeholder mockup-placeholder-short"></div>
              <!-- The Real ImageCard Preview - same component used in MasonryMarketplace -->
              <div class="preview-card-wrapper">
                <!-- Title Row - only show when there's media; text-only posts show title inside ImageCard's empty placeholder -->
                <div
                  class="masonry-item-title-row"
                  v-if="title.trim() && hasMediaSelected"
                >
                  <div class="masonry-item-title">{{ smartTitleCase(title.trim()) }}</div>
                </div>
                <!-- Card -->
                <ImageCard :item="previewItem" />
              </div>
              <div class="mockup-placeholder mockup-placeholder-medium"></div>
            </div>
            <!-- Right column - partially visible placeholder cards -->
            <div class="mockup-column mockup-column-edge">
              <div class="mockup-placeholder mockup-placeholder-medium"></div>
              <div class="mockup-placeholder mockup-placeholder-tall"></div>
            </div>
          </div>
        </div>

        <!-- Phone footer for Preview Mode -->
        <div class="phoneFooter actionBar" v-show="showFooter">
          <div class="actionIcons"></div>
          <UnitButton
            type="button"
            variant="secondary"
            class="post-composer-save-button saveBtn"
            :disabled="isProcessing"
            @click.stop="onInlineSaveClick"
          >
            {{ isProcessing ? "Processing..." : "Save" }}
          </UnitButton>
        </div>
      </div>
      <!-- Normal Edit Mode -->
      <div v-else key="edit" class="phoneFrame" aria-label="Phone preview 1080x1920">
        <div class="phoneViewport">
          <div v-if="!hasMediaSelected && !captureMode" class="textRegion fill">
            <div
              class="previewTitle editable-field"
              contenteditable="true"
              :data-placeholder="titlePlaceholder"
              :class="{ 'editing-active': activeField === 'title' }"
              @input="titleField.onInput"
              @paste="titleField.onPaste"
              @focus="titleField.onFocus"
              @blur="titleField.onBlur"
              :ref="titleField.setRef"
            ></div>
            <div
              class="previewCaption editable-field"
              contenteditable="true"
              :data-placeholder="genericPlaceholder"
              :class="{ 'editing-active': activeField === 'description' }"
              @input="descField.onInput"
              @paste="descField.onPaste"
              @focus="descField.onFocus"
              @blur="descField.onBlur"
              :ref="descField.setRef"
            ></div>
          </div>
          <div v-else class="mediaRegion fill">
            <template v-if="selectedType === 'photos'">
              <template v-if="captureMode && !images.length">
                <video
                  ref="cameraPreviewRef"
                  class="fullMedia"
                  autoplay
                  muted
                  playsinline
                  webkit-playsinline
                ></video>
                <div class="captureOverlay">
                  <button
                    class="switchCameraBtn clickableAnim"
                    @click.stop="switchCamera"
                    :disabled="isRecording"
                    aria-label="Switch camera"
                  >
                    <svg class="compactImg" aria-hidden="true">
                      <use href="@/assets/icons/iconset.svg#flip_compact"></use>
                    </svg>
                  </button>
                  <div class="shutterWrap">
                    <button
                      class="shutterButton"
                      :class="{ recording: isRecording }"
                      @mousedown.prevent="onShutterPressStart"
                      @mouseup.prevent="onShutterPressEnd"
                      @mouseleave="onShutterPressCancel"
                      @touchstart.prevent="onShutterPressStart"
                      @touchend.prevent="onShutterPressEnd"
                      aria-label="Shutter"
                    >
                      <span
                        class="ring"
                        :style="{ '--progress': recordingProgressDeg + 'deg' }"
                      ></span>
                      <span class="dot"></span>
                    </button>
                  </div>
                </div>
              </template>
              <div
                v-else-if="images.length"
                class="mediaPreviewContainer"
                @click="viewMode === 'preview' ? (previewFocused = true) : null"
              >
                <!-- Title block above the image -->
                <div
                  v-if="viewMode === 'edit' || title.trim()"
                  class="imageInfoBlock topBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="titlePlaceholder"
                  :class="{ 'editing-active': activeField === 'title' }"
                  @input="titleField.onInput"
                  @paste="titleField.onPaste"
                  @focus="titleField.onFocus"
                  @blur="titleField.onBlur"
                  :ref="titleField.setRef"
                ></div>
                <!-- Image area fills the remaining space -->
                <div
                  class="imageArea"
                  :class="{ videoStill: isVideoMedia(currentPreviewMedia) }"
                >
                  <template v-if="isVideoMedia(currentPreviewMedia)">
                    <img
                      v-if="thumbnailSrc(currentPreviewMedia)"
                      :src="thumbnailSrc(currentPreviewMedia)"
                      class="imageAreaImg"
                      alt="selected video thumbnail"
                    />
                    <video
                      v-else
                      :src="mediaPreviewSrc(currentPreviewMedia)"
                      class="imageAreaImg"
                      playsinline
                      muted
                      preload="metadata"
                    />
                    <div class="composerVideoOverlay" aria-hidden="true">
                      <svg class="composerVideoPlay" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" fill="currentColor" />
                      </svg>
                    </div>
                  </template>
                  <img
                    v-else
                    :src="thumbnailSrc(currentPreviewMedia)"
                    class="imageAreaImg"
                    alt="selected"
                  />
                </div>
                <!-- Per-image caption block (always uses imageCaptionField, single or multi) -->
                <div
                  v-if="viewMode === 'edit' || currentImageCaption.trim()"
                  class="imageInfoBlock bottomBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="captionPlaceholder"
                  :class="{ 'editing-active': activeField === 'imageCaption' }"
                  @input="imageCaptionField.onInput($event)"
                  @paste="imageCaptionField.onPaste($event)"
                  @focus="imageCaptionField.onFocus()"
                  @blur="imageCaptionField.onBlur()"
                  :ref="imageCaptionField.setRef"
                ></div>
                <!-- Whole-post description block (always shown in edit mode) -->
                <div
                  v-if="viewMode === 'edit' || description.trim()"
                  class="imageInfoBlock descriptionBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="genericPlaceholder"
                  :class="{ 'editing-active': activeField === 'description' }"
                  @input="descField.onInput"
                  @paste="descField.onPaste"
                  @focus="descField.onFocus"
                  @blur="descField.onBlur"
                  :ref="descField.setRef"
                ></div>
                <!-- Inline controls row sits above caption info block -->
                <div
                  v-if="viewMode !== 'preview'"
                  class="controlsRow inlineControls"
                >
                  <InlineMediaTray
                    :items="mediaItemsForTray"
                    :selectedIndex="selectedIndexForTray"
                    :thumbnailFirst="false"
                    :hideEdit="true"
                    @select="onTraySelect"
                    @remove="removeMediaAt"
                    @edit="onTrayEdit"
                    @reorder="onTrayReorderMixed"
                  >
                    <template #append v-if="!isEditingMintedNFT">
                      <label
                        :for="allInputId"
                        class="upload-placeholder"
                        aria-label="Add more media"
                      >
                        <svg class="upload-placeholder-icon" aria-hidden="true">
                          <use href="@/assets/icons/iconset.svg#upload"></use>
                        </svg>
                      </label>
                    </template>
                  </InlineMediaTray>
                </div>
              </div>
              <div v-else class="previewGhost gray">Opening camera…</div>
            </template>
            <template v-else-if="selectedType === 'documents'">
              <div class="mediaPreviewContainer">
                <!-- Title block above the docs list -->
                <div
                  class="imageInfoBlock topBlock editable-field"
                  contenteditable="true"
                  :data-placeholder="titlePlaceholder"
                  :class="{ 'editing-active': activeField === 'title' }"
                  @input="titleField.onInput"
                  @paste="titleField.onPaste"
                  @focus="titleField.onFocus"
                  @blur="titleField.onBlur"
                  :ref="titleField.setRef"
                ></div>
                <div class="docsArea">
                  <div class="docPreview">
                    <!-- PDF: native embed -->
                    <template v-if="isActiveDocPdf && activeDoc?.url">
                      <embed
                        :src="activeDoc.url"
                        type="application/pdf"
                        class="docEmbed"
                      />
                    </template>
                    <!-- Text-readable files (.txt, .md, .csv, .json, etc): show content -->
                    <template v-else-if="isActiveDocText">
                      <div class="docTextPreview">
                        <div class="docTextHeader">
                          <span class="docTypeBadge">{{ activeDocExtension }}</span>
                          <span class="docTextName">{{ activeDoc?.name || 'Document' }}</span>
                        </div>
                        <pre v-if="activeDocTextContent" class="docTextBody">{{ activeDocTextContent }}</pre>
                        <div v-else class="docTextLoading">Reading file…</div>
                      </div>
                    </template>
                    <!-- Office docs (.doc, .docx, .ppt, .xls, etc): rich placeholder -->
                    <template v-else-if="isActiveDocOffice">
                      <div class="docOfficePlaceholder">
                        <div class="docOfficeIcon">
                          <svg class="iconLg">
                            <use href="@/assets/icons/iconset.svg#file"></use>
                          </svg>
                        </div>
                        <span class="docTypeBadge office">{{ activeDocExtension }}</span>
                        <div class="docName">
                          {{ activeDoc?.name || "Document" }}
                        </div>
                        <small class="docOfficeHint">Preview not available for this format</small>
                      </div>
                    </template>
                    <!-- Fallback for unknown types -->
                    <template v-else>
                      <div class="docPlaceholder">
                        <svg class="iconLg">
                          <use href="@/assets/icons/iconset.svg#file"></use>
                        </svg>
                        <div class="docName">
                          {{ activeDoc?.name || "Document" }}
                        </div>
                      </div>
                    </template>
                  </div>
                  <div class="docListInline">
                    <InlineMediaTray
                      :items="mediaItemsForTray"
                      :selectedIndex="selectedIndexForTray"
                      :thumbnailFirst="false"
                      :hideEdit="true"
                      @select="onTraySelect"
                      @remove="removeMediaAt"
                      @edit="onTrayEdit"
                      @reorder="onTrayReorderMixed"
                    >
                      <template #append v-if="!isEditingMintedNFT">
                        <label
                          :for="allInputId"
                          class="upload-placeholder"
                          aria-label="Add more files"
                        >
                          <svg
                            class="upload-placeholder-icon"
                            aria-hidden="true"
                          >
                            <use href="@/assets/icons/iconset.svg#upload"></use>
                          </svg>
                        </label>
                      </template>
                    </InlineMediaTray>
                  </div>
                </div>
                <!-- Caption block below the docs list -->
                <div
                  class="imageInfoBlock bottomBlock editable-field"
                  contenteditable="true"
                  :data-placeholder="captionPlaceholder"
                  :class="{ 'editing-active': activeField === 'caption' }"
                  @input="captionField.onInput"
                  @paste="captionField.onPaste"
                  @focus="captionField.onFocus"
                  @blur="captionField.onBlur"
                  :ref="captionField.setRef"
                ></div>
              </div>
            </template>
            <template v-else-if="selectedType === 'video'">
              <div class="mediaPreviewContainer">
                <!-- Title block above the video -->
                <div
                  class="imageInfoBlock topBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="titlePlaceholder"
                  :class="{ 'editing-active': activeField === 'title' }"
                  @input="titleField.onInput"
                  @paste="titleField.onPaste"
                  @focus="titleField.onFocus"
                  @blur="titleField.onBlur"
                  :ref="titleField.setRef"
                ></div>
                <div class="videoArea" v-if="videoPreviewUrl">
                  <video
                    :src="videoPreviewUrl"
                    :poster="standaloneVideoPosterSrc || undefined"
                    class="fullMedia"
                    playsinline
                    controls
                  />
                </div>
                <div v-else class="videoPlaceholder gray">
                  Video selected (trim up to 60s)
                </div>

                <!-- Caption block below the video -->
                <div
                  class="imageInfoBlock bottomBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="captionPlaceholder"
                  :class="{ 'editing-active': activeField === 'caption' }"
                  @input="captionField.onInput"
                  @paste="captionField.onPaste"
                  @focus="captionField.onFocus"
                  @blur="captionField.onBlur"
                  :ref="captionField.setRef"
                ></div>

                <div
                  v-if="viewMode !== 'preview'"
                  class="controlsRow inlineControls"
                >
                  <InlineMediaTray
                    :items="mediaItemsForTray"
                    :selectedIndex="selectedIndexForTray"
                    :thumbnailFirst="false"
                    :hideEdit="true"
                    @select="onTraySelect"
                    @remove="removeMediaAt"
                    @edit="onTrayEdit"
                    @reorder="onTrayReorderMixed"
                  >
                    <template #append v-if="!isEditingMintedNFT">
                      <label
                        :for="allInputId"
                        class="upload-placeholder"
                        aria-label="Add more media"
                      >
                        <svg class="upload-placeholder-icon" aria-hidden="true">
                          <use href="@/assets/icons/iconset.svg#upload"></use>
                        </svg>
                      </label>
                    </template>
                  </InlineMediaTray>
                </div>

                <!-- Audio Player (Consistent Style) -->
                <div v-if="hasAudio" class="audioPreview videoContext">
                  <div class="audioBadge">
                    <div class="audioHeader">
                      <span class="audioName"
                        >🎵 {{ audioFile?.name || "Audio track" }}</span
                      >
                      <UnitButton
                        variant="text"
                        size="micro"
                        class="post-composer-text-action removeAudioBtn"
                        @click.stop="clearAudio"
                      >
                        Remove
                      </UnitButton>
                    </div>

                    <audio
                      v-if="audioPreviewUrl"
                      :src="audioPreviewUrl"
                      controls
                      class="audioControl"
                    ></audio>
                  </div>
                </div>
              </div>
            </template>
            <template v-else-if="selectedType === 'embed'">
              <div class="mediaPreviewContainer">
                <!-- Title block above the embed -->
                <div
                  class="imageInfoBlock topBlock editable-field embed-title-block"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="titlePlaceholder"
                  :class="{ 'editing-active': activeField === 'title' }"
                  @input="titleField.onInput"
                  @paste="titleField.onPaste"
                  @focus="titleField.onFocus"
                  @blur="titleField.onBlur"
                  :ref="titleField.setRef"
                ></div>
                <!-- YouTube / SoundCloud embed preview -->
                <div
                  v-if="poster.embedHtml"
                  class="embedPreviewArea"
                  :class="{ 'embed-has-error': !!embedError, 'embed-vertical': poster.embedIsVertical }"
                  v-html="poster.embedHtml"
                ></div>
                <div v-else class="videoPlaceholder gray">
                  Paste a YouTube, SoundCloud, or Spotify link
                </div>
                <!-- Unavailable message with tooltip -->
                <div v-if="embedError" class="embedErrorRow">
                  <span class="embedErrorLabel">Unavailable.</span>
                  <HelpHint
                    :message="embedError"
                    icon="Why?"
                    size="small"
                    position="bottom"
                  />
                </div>
                <!-- Editable embed URL -->
                <div v-if="viewMode === 'edit'" class="embed-url-row">
                  <input
                    type="text"
                    class="embed-url-input"
                    v-model="posterInput"
                    @change="onPosterInput"
                    placeholder="Paste YouTube or SoundCloud URL"
                  />
                </div>
                <!-- Caption block below the embed -->
                <div
                  class="imageInfoBlock bottomBlock editable-field"
                  :contenteditable="viewMode === 'edit'"
                  :data-placeholder="captionPlaceholder"
                  :class="{ 'editing-active': activeField === 'caption' }"
                  @input="captionField.onInput"
                  @paste="captionField.onPaste"
                  @focus="captionField.onFocus"
                  @blur="captionField.onBlur"
                  :ref="captionField.setRef"
                ></div>
              </div>
            </template>
            <template v-else-if="selectedType === 'audio'">
              <div class="textRegion fill">
                <div
                  class="previewTitle editable-field"
                  contenteditable="true"
                  :data-placeholder="titlePlaceholder"
                  :class="{ 'editing-active': activeField === 'title' }"
                  @input="titleField.onInput"
                  @paste="titleField.onPaste"
                  @focus="titleField.onFocus"
                  @blur="titleField.onBlur"
                  :ref="titleField.setRef"
                ></div>
                <div
                  class="previewCaption editable-field"
                  contenteditable="true"
                  :data-placeholder="genericPlaceholder"
                  :class="{ 'editing-active': activeField === 'description' }"
                  @input="descField.onInput"
                  @paste="descField.onPaste"
                  @focus="descField.onFocus"
                  @blur="descField.onBlur"
                  :ref="descField.setRef"
                ></div>
              </div>

              <!-- Cover image thumbnail -->
              <div class="audioCoverSection">
                <div v-if="audioCoverPreviewUrl" class="audioCoverThumb">
                  <img
                    :src="audioCoverPreviewUrl"
                    alt="Audio cover"
                    class="audioCoverImg"
                  />
                  <UnitButton
                    variant="text"
                    size="micro"
                    class="post-composer-text-action removeCoverBtn"
                    @click.stop="clearAudioCover"
                  >
                    Remove cover
                  </UnitButton>
                </div>
                <label v-else :for="audioCoverInputId" class="audioCoverPicker">
                  <svg class="compactImg" aria-hidden="true">
                    <use href="@/assets/icons/iconset.svg#upload_compact" />
                  </svg>
                  <span class="audioCoverLabel">Add cover image</span>
                </label>
              </div>

              <!-- Audio Player below text -->
              <div class="audioPreview audioContext">
                <div v-if="hasAudio" class="audioBadge">
                  <div class="audioHeader">
                    <span class="audioName"
                      >🎵 {{ audioFile?.name || "Audio track" }}</span
                    >
                    <UnitButton
                      variant="text"
                      size="micro"
                      class="post-composer-text-action removeAudioBtn"
                      @click.stop="clearAudio"
                    >
                      Remove
                    </UnitButton>
                  </div>

                  <audio
                    v-if="audioPreviewUrl"
                    :src="audioPreviewUrl"
                    controls
                    class="audioControl"
                  ></audio>
                </div>
                <div v-else class="audioPlaceholder gray">
                  Select an audio file
                </div>
              </div>
            </template>
            <template v-else>
              <div class="previewGhost gray">Opening camera…</div>
            </template>
          </div>
          <!-- Upload tray for new posts (no media selected yet) -->
          <div
            v-if="!hasMediaSelected && !captureMode && viewMode !== 'preview'"
            class="footerMediaTray"
          >
            <InlineMediaTray
              :items="[]"
              :selectedIndex="0"
              :thumbnailFirst="false"
              :hideEdit="true"
            >
              <template #append>
                <label
                  :for="allInputId"
                  class="upload-placeholder"
                  aria-label="Add media"
                >
                  <svg class="upload-placeholder-icon" aria-hidden="true">
                    <use href="@/assets/icons/iconset.svg#upload"></use>
                  </svg>
                  <span class="upload-placeholder-label">Upload file</span>
                </label>
              </template>
            </InlineMediaTray>
          </div>
          <!-- Phone footer action bar: always visible, same place for both modes -->
          <div class="phoneFooter actionBar" v-show="showFooter">
            <div class="actionIcons">
              <svg
                v-if="showCameraIcon"
                class="compactImg actionIcon clickableAnim"
                @click="onCameraFooterClick"
                aria-label="Camera"
              >
                <use href="@/assets/icons/iconset.svg#camera" />
              </svg>
              <svg
                class="compactImg actionIcon clickableAnim"
                @click="toggleEmojiPicker"
                aria-label="Emoji"
              >
                <use href="@/assets/icons/iconset.svg#smile" />
              </svg>
            </div>
            <!-- Remove embed — text button, centered between icons and forward arrow -->
            <span
              v-if="selectedType === 'embed' && poster.embedHtml && viewMode !== 'preview'"
              class="remove-embed-btn clickableAnim"
              @click.stop="removeEmbed"
              role="button"
              tabindex="0"
              aria-label="Remove embed"
            >Remove embed</span>
            <div
              v-if="editingItemId"
              class="delete-action"
              :class="{ 'confirm-phase': deleteConfirmPhase }"
              @click.stop="onDeletePost"
              role="button"
              tabindex="0"
              :aria-label="deleteConfirmPhase ? 'Confirm delete' : 'Delete post'"
              :title="deleteConfirmPhase ? 'Click again to delete' : 'Delete'"
            >
              <svg v-if="!deleteConfirmPhase" class="compactImg actionIcon delete-icon" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#trash_compact"></use>
              </svg>
              <span v-else class="delete-question">?</span>
            </div>
            <UnitButton
              v-if="viewMode !== 'preview'"
              type="button"
              variant="secondary"
              size="small"
              class="forwardBtn"
              :disabled="!hasPreview"
              @click.stop="onNext"
              icon-only
              aria-label="Next"
            >
              <svg class="compactImg" aria-hidden="true">
                <use href="@/assets/icons/iconset.svg#arrowright"></use>
              </svg>
            </UnitButton>
            <UnitButton
              v-else
              type="button"
              variant="secondary"
              class="post-composer-save-button saveBtn"
              :disabled="isProcessing"
              @click.stop="onInlineSaveClick"
            >
              {{ isProcessing ? "Processing..." : "Save" }}
            </UnitButton>
          </div>
        </div>
      </div>
    </div>

    <EmojiAndMediaPicker
      :isVisible="isEmojiPickerOpen"
      pickerMode="emoji"
      :recent-emojis="recentEmojis"
      @emojiSelected="handleEmojiSelected"
      @closePicker="isEmojiPickerOpen = false"
    />

    <div v-if="isMediaMenuOpen" class="mediaMenu card shadow">
      <label
        class="media-menu__file-action"
        :for="imageInputId"
        role="button"
        tabindex="0"
        @click="chooseType('photos')"
        @keydown.enter.prevent="activateMediaInput('photos', imageInput)"
        @keydown.space.prevent="activateMediaInput('photos', imageInput)"
        >Photos</label
      >
      <label
        class="media-menu__file-action"
        :for="videoInputId"
        role="button"
        tabindex="0"
        @click="chooseType('video')"
        @keydown.enter.prevent="activateMediaInput('video', videoInput)"
        @keydown.space.prevent="activateMediaInput('video', videoInput)"
        >Video</label
      >
      <label
        class="media-menu__file-action"
        :for="docInputId"
        role="button"
        tabindex="0"
        @click="chooseType('documents')"
        @keydown.enter.prevent="activateMediaInput('documents', docInput)"
        @keydown.space.prevent="activateMediaInput('documents', docInput)"
        >Documents</label
      >
      <label
        class="media-menu__file-action"
        :for="audioInputId"
        role="button"
        tabindex="0"
        @click="chooseType('audio')"
        @keydown.enter.prevent="activateMediaInput('audio', audioInput)"
        @keydown.space.prevent="activateMediaInput('audio', audioInput)"
        >Audio</label
      >
    </div>

    <input
      ref="imageInput"
      :id="imageInputId"
      type="file"
      accept="image/*"
      :capture="isMobile() ? 'environment' : null"
      :multiple="!isMobile()"
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickImages"
    />
    <input
      ref="imageLibraryInput"
      :id="imageLibraryInputId"
      type="file"
      accept="image/*"
      multiple
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickImages"
    />
    <input
      ref="videoInput"
      :id="videoInputId"
      type="file"
      accept="video/*"
      multiple
      :capture="isMobile() ? 'environment' : null"
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickVideo"
    />
    <input
      ref="videoLibraryInput"
      :id="videoLibraryInputId"
      type="file"
      accept="video/*"
      multiple
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickVideo"
    />
    <input
      ref="docInput"
      :id="docInputId"
      type="file"
      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
      multiple
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickDocuments"
    />
    <input
      ref="audioInput"
      :id="audioInputId"
      type="file"
      accept="audio/*"
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickAudio"
    />
    <input
      ref="audioCoverInput"
      :id="audioCoverInputId"
      type="file"
      accept="image/*"
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickAudioCover"
    />
    <input
      ref="allInput"
      :id="allInputId"
      type="file"
      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
      multiple
      style="
        display: block;
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      "
      tabindex="-1"
      @change="onPickMixedFiles"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import ImageUpload from "@/components/GenericComponents/ImageUpload.vue";
import ImageCard from "@/components/DashboardComponents/EcosystemComponents/MasonryMarketplaceComponents/ImageCard.vue";
import PostPresentationPreview from "@/components/PostComposer/PostPresentationPreview.vue";
import { smartTitleCase } from "@/utils/smartTitleCase";
import {
  detectEmbed,
  sanitizeEmbedHtml,
  generateGroupId,
} from "@/utils/postComposerUtils";
import {
  PRESENTATION_MODES,
  resolvePresentationMode,
} from "@/utils/postPresentation";
import { deriveTextualMediaType } from "@/utils/textualMediaType";
import { useStore } from "vuex";
import {
  savePhotosPost,
  savePhotosPostWithProgress,
  saveTextualPost,
} from "@/services/postComposerService";
import EmojiAndMediaPicker from "@/components/MessagingComponents/EmojiAndMediaPicker.vue";
import InlineMediaTray from "@/components/GenericComponents/InlineMediaTray.vue";
import HelpHint from "@/components/GenericComponents/HelpHint.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import { usePostComposerCamera } from "@/composables/usePostComposerCamera";
import { usePostComposerTray } from "@/composables/usePostComposerTray";
import { useEditableField, insertTextAtCaret } from "@/composables/useEditableField";
import { createPublicationOperationId } from "@/services/trustedPublicationService";

export default {
  name: "PostComposer",
  components: {
    ImageUpload,
    EmojiAndMediaPicker,
    InlineMediaTray,
    ImageCard,
    PostPresentationPreview,
    HelpHint,
    UnitButton,
  },
  props: {
    isProcessing: { type: Boolean, default: false },
  },
  emits: [
    "update-canPreview",
    "submit",
    "next",
    "cancel",
    "close",
    "saved",
    "open-sale",
    "make-offer",
  ],
  setup(props, { emit, expose }) {
    const loadedMediaVariantsByUrl = new Map();
    const viewMode = ref("edit"); // 'edit' | 'preview'
    const goToPreview = () => {
      if (!hasPreview.value) return;
      viewMode.value = "preview";
      previewFocused.value = true;
    };
    const onNext = () => {
      log("onNext called", {
        hasPreview: hasPreview.value,
        isNftMintPending: isNftMintPending.value,
      });
      if (!hasPreview.value) {
        log("onNext: no preview, returning early");
        return;
      }
      // If NFT mint is selected, open the NFT drawer flow instead of normal preview
      if (isNftMintPending.value) {
        log("onNext: NFT pending, emitting open-sale");
        isNftMintPending.value = false; // reset the state
        emit("open-sale");
        return;
      }
      goToPreview();
      emit("next"); // let parent Overlay show its back arrow
    };
    const onInlineSaveClick = () => {
      emit("submit");
    };
    const creationOperationId = ref(null);
    const ensureCreationOperationId = () => {
      if (!creationOperationId.value) {
        creationOperationId.value = createPublicationOperationId();
      }
      return creationOperationId.value;
    };
    const resetDraft = () => {
      title.value = "";
      caption.value = "";
      description.value = "";
      imageCaptions.value = [];
      selectedType.value = null;
      images.value = [];
      loadedMediaVariantsByUrl.clear();
      thumbnailIndex.value = 0;
      documents.value = [];
      clearVideoPosterPreviews();
      videoFile.value = null;
      existingVideoUrl.value = "";
      audioFile.value = null;
      audioCoverImage.value = null;
      existingAudioUrl.value = "";
      poster.value = { url: "", embedHtml: "", meta: {} };
      posterInput.value = "";
      captureMode.value = false;
      previewFocused.value = false;
      viewMode.value = "edit";
      isEditingMintedNFT.value = false;
      editingItemId.value = null;
      editingItemCollection.value = null;
      deleteConfirmPhase.value = false;
      creationOperationId.value = null;
      if (deleteConfirmTimeout) {
        clearTimeout(deleteConfirmTimeout);
        deleteConfirmTimeout = null;
      }
      // Sync contenteditable DOM after clearing refs
      nextTick(() => {
        titleField.sync();
        descField.sync();
        captionField.sync();
        imageCaptionField.sync();
      });
    };
    // Track if we're editing an existing minted NFT (disables media upload)
    const isEditingMintedNFT = ref(false);
    // Track the ID of the item being edited (null = new post)
    const editingItemId = ref(null);
    const editingItemCollection = ref(null);
    // Inline delete/discard confirmation phase
    const deleteConfirmPhase = ref(false);
    let deleteConfirmTimeout = null;
    const isSaving = ref(false);
    const isDirty = computed(() => {
      const hasText =
        (title.value && title.value.trim().length) ||
        (caption.value && caption.value.trim().length) ||
        (description.value && description.value.trim().length);
      return !!(hasMediaSelected.value || hasText);
    });
    const onCancel = () => {
      try {
        resetDraft();
        emit("cancel");
        emit("close");
      } catch {
        resetDraft();
        emit("cancel");
        emit("close");
      }
    };
    const store = useStore();
    const log = (...args) => {
      try {
        // eslint-disable-next-line no-console
        console.log("[PostComposer]", ...args);
      } catch {}
    };
    const title = ref("");
    const caption = ref(""); // Legacy - maps to description for single-image, or current image caption for multi
    const description = ref(""); // Whole-post description (distinct from per-image captions)

    // Generic placeholders that work for all cases
    const genericPlaceholder = "What's on your mind?";
    const titlePlaceholder = "Add a title...";

    // Dynamic caption placeholder based on media type
    const captionPlaceholder = computed(() => {
      if (selectedType.value === "video") return "Caption this...";
      return "Caption this image...";
    });

    // --- Inline editable fields (replaces proxy textarea) ---
    const activeField = ref(null); // 'title' | 'description' | 'caption' | 'imageCaption'
    const activeFieldEl = ref(null); // last-focused contenteditable element

    // Auto-detect YouTube/SoundCloud URLs pasted into text fields
    const handlePastedUrl = async (text) => {
      const url = (text || "").trim();
      console.log("[PostComposer] handlePastedUrl called", { url, selectedType: selectedType.value, hasMediaSelected: hasMediaSelected.value });
      if (!url) return;
      // Only auto-detect when no other media is attached
      if (
        selectedType.value &&
        selectedType.value !== "poster" &&
        selectedType.value !== "embed" &&
        hasMediaSelected.value
      ) {
        console.log("[PostComposer] handlePastedUrl: skipped — other media selected", selectedType.value);
        return;
      }
      // Quick regex gate — only try YouTube/SoundCloud/Spotify patterns
      // Supports all YouTube subdomains: music.youtube.com, m.youtube.com, etc.
      const EMBED_RE =
        /^(https?:\/\/)?([\w-]+\.)*(youtube\.com|youtu\.be|soundcloud\.com|open\.spotify\.com)\//i;
      if (!EMBED_RE.test(url)) {
        console.log("[PostComposer] handlePastedUrl: URL does not match embed regex", url);
        return;
      }
      console.log("[PostComposer] handlePastedUrl: URL matched! calling detectEmbed...");
      const embed = await detectEmbed(url);
      console.log("[PostComposer] handlePastedUrl: detectEmbed result", embed);
      if (!embed?.html) {
        console.log("[PostComposer] handlePastedUrl: no embed html returned");
        return;
      }
      // Switch to embed type and populate embed data
      console.log("[PostComposer] handlePastedUrl: switching to embed type");
      selectedType.value = "embed";
      clearOtherTypes("embed");
      poster.value = {
        url,
        embedHtml: sanitizeEmbedHtml(embed.html),
        embedProvider: embed.provider || null,
        thumbnailUrl: embed.thumbnailUrl || "",
        meta: embed.meta || {},
        embedIsVertical: embed.embedIsVertical || false,
        embedIsSquare: embed.embedIsSquare || false,
      };
      posterInput.value = url;
      embedError.value = ""; // Reset error on new embed
      console.log("[PostComposer] handlePastedUrl: poster.value set", JSON.parse(JSON.stringify(poster.value)));
    };

    // YouTube iframe sends postMessage events including error state
    // We listen for the "onError" event to detect unavailable videos
    const handleYouTubeMessage = (event) => {
      try {
        // Only process messages from YouTube origins
        if (
          !event.origin ||
          (!event.origin.includes("youtube.com") &&
            !event.origin.includes("youtube-nocookie.com"))
        )
          return;
        // YouTube sends JSON strings via postMessage
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!data) return;
        console.log("[PostComposer] YouTube postMessage", data);
        // YouTube Player API error codes:
        // 2 = invalid parameter, 5 = HTML5 error,
        // 100 = not found / removed, 101/150 = embed disabled by owner
        const errorCode =
          data?.event === "onError"
            ? data?.info
            : data?.info?.playerState === -1 && data?.info?.errorCode
            ? data.info.errorCode
            : null;
        if (errorCode != null && (selectedType.value === "embed" || selectedType.value === "poster")) {
          const reasons = {
            2: "The video URL contains an invalid parameter.",
            5: "This video cannot be played in an embed (HTML5 error).",
            100: "This video has been removed or is private.",
            101: "The video owner does not allow embedded playback.",
            150: "The video owner does not allow embedded playback.",
          };
          embedError.value =
            reasons[errorCode] ||
            `YouTube returned error code ${errorCode}.`;
          console.log("[PostComposer] YouTube embed error", { errorCode, message: embedError.value });
        }
      } catch {
        // Not a JSON message or not from YouTube — ignore
      }
    };

    /** Remove the embed preview but keep the original URL as text in the title field */
    const removeEmbed = () => {
      const embedUrl = poster.value?.url || poster.value?.embedUrl || posterInput.value || "";
      // Clear embed data
      poster.value = { url: "", embedHtml: "", meta: {} };
      posterInput.value = "";
      embedError.value = "";
      selectedType.value = null;
      // Preserve the URL in the title so the user keeps their link as plain text
      if (embedUrl && !title.value) {
        title.value = embedUrl;
      }
      log("removeEmbed: embed removed, URL preserved in title", { embedUrl });
    };

    const titleField = useEditableField(title, {
      placeholder: titlePlaceholder,
      onFocusCb: () => { activeField.value = "title"; activeFieldEl.value = titleField.fieldRef.value; },
      onPasteCb: handlePastedUrl,
    });
    const descField = useEditableField(description, {
      placeholder: genericPlaceholder,
      onFocusCb: () => { activeField.value = "description"; activeFieldEl.value = descField.fieldRef.value; },
      onPasteCb: handlePastedUrl,
    });
    const captionField = useEditableField(caption, {
      placeholder: "Caption this...",
      onFocusCb: () => { activeField.value = "caption"; activeFieldEl.value = captionField.fieldRef.value; },
    });
    // imageCaptionField uses the computed currentImageCaption (get/set)
    // — defined after currentImageCaption below; see imageCaptionField initialization

    const mobileImageAccept = computed(() =>
      isMobile() ? "image/*;capture=camera" : "image/*",
    );
    const mobileVideoAccept = computed(() =>
      isMobile() ? "video/*;capture=camera" : "video/*",
    );

    // Insert emoji at caret in whichever contenteditable field is active
    const insertAtCaret = (text) => {
      insertTextAtCaret(activeFieldEl.value, text, description, descField.sync);
    };

    // Emoji picker state
    const isEmojiPickerOpen = ref(false);
    const recentEmojis = ref([]);
    const toggleEmojiPicker = () => {
      isEmojiPickerOpen.value = !isEmojiPickerOpen.value;
      log("toggleEmojiPicker", { open: isEmojiPickerOpen.value });
    };
    const handleEmojiSelected = (emoji) => {
      insertAtCaret(emoji);
      // Persist recents (max 20)
      try {
        const current = new Set([emoji, ...(recentEmojis.value || [])]);
        recentEmojis.value = Array.from(current).slice(0, 20);
        localStorage.setItem(
          "pc_recent_emojis",
          JSON.stringify(recentEmojis.value),
        );
      } catch {}
    };


    const onDeletePost = async () => {
      if (deleteConfirmPhase.value) {
        // Second click - confirm action
        if (deleteConfirmTimeout) {
          clearTimeout(deleteConfirmTimeout);
          deleteConfirmTimeout = null;
        }
        deleteConfirmPhase.value = false;
        if (editingItemId.value) {
          // Editing existing item - delete from database
          try {
            // Map PostComposer selectedType to the mediaType the store expects
            const mediaTypeMap = { photos: "image", video: "video", audio: "audio", embed: "image", poster: "image" };
            await store.dispatch("mediaActions/deleteItem", {
              id: editingItemId.value,
              type: "media",
              mediaType: mediaTypeMap[selectedType.value] || "image",
              images: images.value.filter((i) => typeof i === "string"),
            });
            try {
              window?.$toast?.success?.("Post deleted");
            } catch {}
          } catch (err) {
            console.error("[PostComposer] Delete failed:", err);
            try {
              window?.$toast?.error?.("Failed to delete post");
            } catch {}
            return;
          }
        } else {
          // New post - just discard
          try {
            window?.$toast?.warning?.("Draft discarded");
          } catch {}
        }
        resetDraft();
        emit("cancel");
        emit("close");
      } else {
        // First click - show confirmation
        deleteConfirmPhase.value = true;
        // Auto-reset after 3 seconds
        deleteConfirmTimeout = setTimeout(() => {
          deleteConfirmPhase.value = false;
        }, 3000);
      }
    };
    // Track when NFT mint icon is "selected" (waiting for forward button)
    const isNftMintPending = ref(false);

    const onMintNFTClick = () => {
      // Embed posts are not mintable
      if (selectedType.value === "embed") {
        log("NFT mint blocked: embed posts are not mintable");
        return;
      }
      if (!hasMediaSelected.value) {
        // No media yet - open file picker and keep menu open to show lilac state
        isNftMintPending.value = true; // highlight the icon

        openUploadPicker();
        log("NFT mint requested; opening media picker");
      } else {
        // Toggle selection - keep menu open to show lilac state
        isNftMintPending.value = !isNftMintPending.value;
        log("NFT mint toggled", {
          selected: isNftMintPending.value,
        });
        // Don't close menu - user can see the lilac highlight
      }
    };

    // Media menu state
    const isMediaMenuOpen = ref(false);
    const toggleMediaMenu = () => {
      isMediaMenuOpen.value = !isMediaMenuOpen.value;
    };
    const openUploadPicker = () => {
      viewMode.value = "edit";
      const fallback = () =>
        (
          imageLibraryInput.value ||
          imageInput.value ||
          videoLibraryInput.value ||
          videoInput.value
        )?.click?.();
      try {
        if (allInput.value && typeof allInput.value.click === "function") {
          allInput.value.click();
        } else {
          fallback();
        }
      } catch {
        fallback();
      }
    };

    const selectedType = ref(null); // 'photos' | 'video' | 'documents' | 'audio' | 'embed' | 'poster'
    const previewFocused = ref(false);

    // Photos
    const images = ref([]); // File[] or string URLs
    const imageCaptions = ref([]); // Per-image captions, maps 1:1 with images[]
    const thumbnailIndex = ref(0);

    // Sync imageCaptions array length with images array
    const syncImageCaptions = () => {
      const len = images.value.length;
      if (imageCaptions.value.length < len) {
        // Extend with empty strings
        while (imageCaptions.value.length < len) {
          imageCaptions.value.push("");
        }
      } else if (imageCaptions.value.length > len) {
        // Trim to match
        imageCaptions.value.length = len;
      }
    };

    // Get/set caption for current thumbnail
    const currentImageCaption = computed({
      get() {
        const idx = thumbnailIndex.value;
        if (idx >= 0 && idx < imageCaptions.value.length) {
          return imageCaptions.value[idx] || "";
        }
        return "";
      },
      set(val) {
        const idx = thumbnailIndex.value;
        syncImageCaptions();
        if (idx >= 0 && idx < imageCaptions.value.length) {
          imageCaptions.value[idx] = val;
        }
      },
    });

    // Editable field for per-image captions (uses computed get/set)
    const imageCaptionField = useEditableField(currentImageCaption, {
      placeholder: "Caption this image...",
      onFocusCb: () => { activeField.value = "imageCaption"; activeFieldEl.value = imageCaptionField.fieldRef.value; },
    });

    // captionOrDescSetRef removed — single-image now always uses imageCaptionField directly

    // When user switches thumbnail, sync the imageCaptionField DOM
    watch(
      () => thumbnailIndex.value,
      () => { nextTick(() => imageCaptionField.sync()); },
    );

    // NOTE: hasMediaSelected watcher moved below its computed definition (see after hasMediaSelected)

    // Check if this is a multi-image post
    const isMultiImagePost = computed(() => images.value.length > 1);

    // Display caption for the EDIT screen: always per-image caption now
    const displayCaption = computed(() => {
      return currentImageCaption.value || "";
    });

    // Display text for the PREVIEW screen: prioritize description over caption
    const previewDisplayText = computed(() => {
      // Always prefer description first (whole-post description)
      const descVal = description.value?.trim() || "";
      const capVal = caption.value?.trim() || "";
      return descVal || capVal;
    });

    // Keep selectedType in sync with current media during edit/create
    const updateSelectedTypeFromContent = () => {
      try {
        // If a video file or existing video URL is present, prioritize video
        if (videoFile.value || existingVideoUrl.value) {
          selectedType.value = "video";
          return;
        }
        // If there is an audio file or existing audio URL, it's an audio post
        if (audioFile.value || existingAudioUrl.value) {
          selectedType.value = "audio";
          return;
        }
        // If there are documents and no video/audio, it's a documents post
        if (Array.isArray(documents.value) && documents.value.length > 0) {
          selectedType.value = "documents";
          return;
        }
        // If embed data is present (YouTube/SoundCloud), it's an embed post
        if (poster.value.embedHtml || poster.value.url) {
          selectedType.value = "embed";
          return;
        }
        // If there are images, it's a photos post
        if (Array.isArray(images.value) && images.value.length > 0) {
          selectedType.value = "photos";
          return;
        }
        // No media selected => convert to textual poster per requirement
        selectedType.value = "poster";
      } catch {}
    };

    // Documents (file-based preview support)
    const documents = ref([]); // { id, name, url, file?, type? }
    const activeDocIndex = ref(0);
    const activeDoc = computed(() =>
      Array.isArray(documents.value) && documents.value.length
        ? documents.value[
            Math.max(
              0,
              Math.min(activeDocIndex.value, documents.value.length - 1),
            )
          ]
        : null,
    );

    // Text content cache for readable document types (.txt, .md, .csv, .json, etc.)
    const docTextContents = ref({}); // { [docId]: string }

    // Helpers to classify document types
    const isTextReadable = (doc) => {
      if (!doc) return false;
      const name = (doc.name || "").toLowerCase();
      const type = (doc.type || "").toLowerCase();
      return (
        type === "text/plain" ||
        type === "text/markdown" ||
        type === "text/csv" ||
        type === "application/json" ||
        type === "text/html" ||
        type === "text/xml" ||
        name.endsWith(".txt") ||
        name.endsWith(".md") ||
        name.endsWith(".csv") ||
        name.endsWith(".json") ||
        name.endsWith(".xml") ||
        name.endsWith(".html") ||
        name.endsWith(".htm")
      );
    };
    const isPdfDoc = (doc) => {
      if (!doc) return false;
      const type = (doc.type || "").toLowerCase();
      const name = (doc.name || "").toLowerCase();
      return type === "application/pdf" || name.endsWith(".pdf");
    };
    const isOfficeDoc = (doc) => {
      if (!doc) return false;
      const name = (doc.name || "").toLowerCase();
      const type = (doc.type || "").toLowerCase();
      return (
        name.endsWith(".doc") ||
        name.endsWith(".docx") ||
        name.endsWith(".ppt") ||
        name.endsWith(".pptx") ||
        name.endsWith(".xls") ||
        name.endsWith(".xlsx") ||
        type.includes("officedocument") ||
        type === "application/msword" ||
        type === "application/vnd.ms-powerpoint" ||
        type === "application/vnd.ms-excel"
      );
    };
    // Detect file extension for display badge
    const docExtension = (doc) => {
      if (!doc?.name) return "";
      const parts = doc.name.split(".");
      return parts.length > 1 ? parts.pop().toUpperCase() : "";
    };

    // Computed: is the active doc a text-readable file?
    const isActiveDocText = computed(() => isTextReadable(activeDoc.value));
    const isActiveDocPdf = computed(() => isPdfDoc(activeDoc.value));
    const isActiveDocOffice = computed(() => isOfficeDoc(activeDoc.value));
    const activeDocExtension = computed(() => docExtension(activeDoc.value));
    // Text content of the currently active text document
    const activeDocTextContent = computed(() => {
      const doc = activeDoc.value;
      if (!doc || !isTextReadable(doc)) return "";
      return docTextContents.value[doc.id] || "";
    });

    // Read text files into the cache when documents change
    watch(
      documents,
      (docs) => {
        if (!Array.isArray(docs)) return;
        docs.forEach((doc) => {
          if (
            isTextReadable(doc) &&
            doc.file &&
            !docTextContents.value[doc.id]
          ) {
            const reader = new FileReader();
            reader.onload = () => {
              docTextContents.value = {
                ...docTextContents.value,
                [doc.id]: reader.result || "",
              };
            };
            reader.readAsText(doc.file);
          }
        });
      },
      { immediate: true },
    );

    // Video/audio placeholders
    const existingVideoUrl = ref("");
    const videoFile = ref(null);
    const videoPosterPreviewRevision = ref(0);
    const videoPosterPreviewUrlMap = new WeakMap();
    const videoPosterPreviewItems = new Set();
    const videoPosterObjectUrls = new Set();
    const videoPosterPreviewByUrl = ref({});
    const looksVideoMediaUrl = (url) =>
      /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url || "");
    const isBlobLikeMedia = (item) =>
      typeof Blob !== "undefined" && item instanceof Blob;
    const isVideoMedia = (item) => {
      if (!item) return false;
      if (typeof item === "string") return looksVideoMediaUrl(item);
      return (
        isBlobLikeMedia(item) &&
        String(item.type || "").toLowerCase().startsWith("video/")
      );
    };
    const mediaTypeForItem = (item) => (isVideoMedia(item) ? "video" : "image");
    // Memoize video object URLs to avoid churn/leaks
    const videoObjectUrlMap = new WeakMap();
    const getVideoObjectUrl = (file) => {
      try {
        let url = videoObjectUrlMap.get(file);
        if (!url) {
          url = URL.createObjectURL(file);
          videoObjectUrlMap.set(file, url);
        }
        return url;
      } catch {
        return "";
      }
    };
    const revokeVideoObjectUrl = (file) => {
      try {
        const url = videoObjectUrlMap.get(file);
        if (url) {
          URL.revokeObjectURL(url);
          videoObjectUrlMap.delete(file);
        }
      } catch {}
    };
    const videoPreviewUrl = computed(() =>
      videoFile.value
        ? getVideoObjectUrl(videoFile.value)
        : existingVideoUrl.value || "",
    );
    const setVideoPosterPreviewForUrl = (videoUrl, posterUrl) => {
      if (!videoUrl || !posterUrl) return;
      videoPosterPreviewByUrl.value = {
        ...videoPosterPreviewByUrl.value,
        [videoUrl]: posterUrl,
      };
      videoPosterPreviewRevision.value += 1;
    };
    const revokeVideoPosterPreview = (item) => {
      try {
        if (!item) return;
        if (isBlobLikeMedia(item)) {
          const url = videoPosterPreviewUrlMap.get(item);
          if (url) {
            URL.revokeObjectURL(url);
            videoPosterObjectUrls.delete(url);
            videoPosterPreviewUrlMap.delete(item);
            videoPosterPreviewItems.delete(item);
            videoPosterPreviewRevision.value += 1;
          }
          return;
        }
        if (typeof item === "string" && videoPosterPreviewByUrl.value[item]) {
          const next = { ...videoPosterPreviewByUrl.value };
          delete next[item];
          videoPosterPreviewByUrl.value = next;
          videoPosterPreviewRevision.value += 1;
        }
      } catch {}
    };
    const clearVideoPosterPreviews = () => {
      try {
        for (const url of videoPosterObjectUrls) URL.revokeObjectURL(url);
        for (const item of videoPosterPreviewItems)
          videoPosterPreviewUrlMap.delete(item);
        videoPosterObjectUrls.clear();
        videoPosterPreviewItems.clear();
        videoPosterPreviewByUrl.value = {};
        videoPosterPreviewRevision.value += 1;
      } catch {}
    };
    const videoPosterPreviewUrl = (item) => {
      videoPosterPreviewRevision.value;
      if (!item) return "";
      if (isBlobLikeMedia(item)) return videoPosterPreviewUrlMap.get(item) || "";
      if (typeof item === "string")
        return videoPosterPreviewByUrl.value[item] || "";
      return "";
    };
    const mediaPreviewSrc = (item) => {
      if (!item) return "";
      if (typeof item === "string") return item;
      if (isVideoMedia(item)) return getVideoObjectUrl(item);
      return getImageObjectUrl(item);
    };
    const ensureVideoPosterPreview = async (item) => {
      if (!isVideoMedia(item) || !isBlobLikeMedia(item)) return;
      if (videoPosterPreviewUrlMap.has(item)) return;
      try {
        const { captureVideoPosterFrame } = await import("@/utils/uploadUtils.js");
        const frame = await captureVideoPosterFrame(item, {
          atTimeSec: 0.2,
          createObjectUrl: true,
        });
        if (!frame?.objectUrl) return;
        const stillSelected =
          videoFile.value === item ||
          (Array.isArray(images.value) && images.value.includes(item));
        if (!stillSelected) {
          URL.revokeObjectURL(frame.objectUrl);
          return;
        }
        videoPosterPreviewUrlMap.set(item, frame.objectUrl);
        videoPosterPreviewItems.add(item);
        videoPosterObjectUrls.add(frame.objectUrl);
        videoPosterPreviewRevision.value += 1;
      } catch (e) {
        console.warn("[PostComposer] Failed to create local video poster:", e);
      }
    };
    const standaloneVideoPosterSrc = computed(() =>
      videoFile.value
        ? videoPosterPreviewUrl(videoFile.value)
        : videoPosterPreviewUrl(existingVideoUrl.value),
    );
    const existingAudioUrl = ref("");
    const audioFile = ref(null);
    const audioCoverImage = ref(null);
    const audioCoverObjectUrlMap = new WeakMap();
    const getAudioCoverObjectUrl = (file) => {
      try {
        let url = audioCoverObjectUrlMap.get(file);
        if (!url) {
          url = URL.createObjectURL(file);
          audioCoverObjectUrlMap.set(file, url);
        }
        return url;
      } catch {
        return "";
      }
    };
    const audioCoverPreviewUrl = computed(() =>
      audioCoverImage.value
        ? getAudioCoverObjectUrl(audioCoverImage.value)
        : "",
    );
    const audioObjectUrlMap = new WeakMap();
    const getAudioObjectUrl = (file) => {
      try {
        let url = audioObjectUrlMap.get(file);
        if (!url) {
          url = URL.createObjectURL(file);
          audioObjectUrlMap.set(file, url);
        }
        return url;
      } catch {
        return "";
      }
    };
    const audioPreviewUrl = computed(() =>
      audioFile.value
        ? getAudioObjectUrl(audioFile.value)
        : existingAudioUrl.value || "",
    );

    // Poster (links/embeds)
    const posterInput = ref("");
    const posterUrlInputRef = ref(null);
    const poster = ref({ url: "", embedHtml: "", meta: {} });
    const embedError = ref(""); // YouTube/SoundCloud error message (e.g. "Video unavailable")

    const hasMediaSelected = computed(() => {
      if (selectedType.value === "photos") return images.value.length > 0;
      if (selectedType.value === "video")
        return !!videoFile.value || !!existingVideoUrl.value;
      if (selectedType.value === "documents") return documents.value.length > 0;
      if (selectedType.value === "embed")
        return !!poster.value.url || !!poster.value.embedHtml;
      if (selectedType.value === "poster")
        return !!poster.value.url || !!poster.value.embedHtml;
      if (selectedType.value === "audio")
        return !!audioFile.value || !!existingAudioUrl.value;
      return false;
    });

    // Sync all contenteditable fields when switching between textRegion ↔ mediaRegion
    // or between media types — the old DOM nodes are destroyed and new ones mounted,
    // so we need to push the ref values into the freshly-created contenteditable elements.
    watch(
      [() => hasMediaSelected.value, () => selectedType.value],
      (newVals, oldVals) => {
        // When switching from text-only (no media) to a media type,
        // port description → caption so the user's text isn't lost
        const [newHasMedia] = newVals || [];
        const [oldHasMedia] = oldVals || [];
        if (newHasMedia && !oldHasMedia) {
          const desc = description.value?.trim();
          if (desc && !caption.value?.trim()) {
            caption.value = desc;
          }
        }
        nextTick(() => {
          syncImageCaptions();
          titleField.sync();
          descField.sync();
          captionField.sync();
          imageCaptionField.sync();
        });
      },
    );

    const hasPreview = computed(() => {
      // Allow text-only posts (title, caption, or description without media)
      const hasText = !!(
        title.value.trim() ||
        caption.value.trim() ||
        description.value.trim()
      );
      if (selectedType.value === "photos")
        return images.value.length > 0 || hasText;
      if (selectedType.value === "documents") return documents.value.length > 0;
      if (selectedType.value === "video")
        return !!videoFile.value || !!existingVideoUrl.value || hasText;
      if (selectedType.value === "audio")
        return !!audioFile.value || !!existingAudioUrl.value || hasText;
      // Embed posts — allow even without text (embed-only)
      if (selectedType.value === "embed")
        return !!(poster.value.embedHtml || poster.value.url || hasText);
      // Poster / embed posts — allow even without text (embed-only)
      if (selectedType.value === "poster")
        return !!(poster.value.embedHtml || poster.value.url || hasText);
      // Default: allow if there's any text (text-only posts)
      return hasText;
    });

    // Construct a preview item object that matches what ImageCard expects
    // This ensures the preview looks exactly like it will in MasonryMarketplace
    // For the PREVIEW screen: description takes priority over per-image captions
    const previewItem = computed(() => {
      // Sync imageCaptions before building preview
      syncImageCaptions();

      const item = {
        id: "preview",
        type: "media",
        // For preview: pass description as caption so ImageCard shows it
        // Don't pass imageCaptions - we want description to take priority in preview
        // (resolveCardText prioritizes imageCaptions for multi-image, so omit them here)
        caption: previewDisplayText.value,
        description: description.value.trim(),
        // imageCaptions intentionally omitted - preview should show description, not per-image captions
        title: title.value.trim(),
        itemName: title.value.trim(),
        mediaType: selectedType.value || "photos",
        thumbnailIndex: thumbnailIndex.value,
      };

      // Build images array from current state
      if (selectedType.value === "photos" && images.value.length > 0) {
        item.images = images.value.map((img) => mediaPreviewSrc(img));
        item.mediaTypes = images.value.map((img) => mediaTypeForItem(img));
        const mediaThumbnails = images.value.map((img) =>
          isVideoMedia(img) ? videoPosterPreviewUrl(img) : "",
        );
        if (mediaThumbnails.some(Boolean)) {
          item.mediaThumbnails = mediaThumbnails;
        }
        item.mediaType = "photos";
      } else if (selectedType.value === "video") {
        // Pass all images so ImageCard can pick the correct poster via thumbnailIndex
        if (images.value.length > 0) {
          item.images = images.value.map((img) => thumbnailSrc(img));
        }
        item.mediaType = "video";
        item.mediaUrl = videoPreviewUrl.value;
        if (standaloneVideoPosterSrc.value) {
          item.mediaThumbnails = [standaloneVideoPosterSrc.value];
        }
      } else if (selectedType.value === "audio") {
        // Audio with optional cover image
        if (audioCoverPreviewUrl.value) {
          item.images = [audioCoverPreviewUrl.value];
        } else if (images.value.length > 0) {
          item.images = [thumbnailSrc(images.value[0])];
        }
        item.mediaType = "audio";
        item.audioUrl = audioPreviewUrl.value;
      } else if (
        selectedType.value === "documents" &&
        documents.value.length > 0
      ) {
        item.mediaType = "documents";
        item.documents = documents.value;
        // Pass text contents so ImageCard preview can show text file content
        if (Object.keys(docTextContents.value).length) {
          item.documentTextContents = { ...docTextContents.value };
        }
      } else if (selectedType.value === "embed") {
        item.mediaType = "embed";
        item.embedHtml = poster.value.embedHtml;
        item.embedUrl = poster.value.url;
        item.embedProvider = poster.value.embedProvider || null;
        item.thumbnailUrl = poster.value.thumbnailUrl || "";
        item.embedIsVertical = poster.value.embedIsVertical || false;
        item.embedIsSquare = poster.value.embedIsSquare || false;
        // Sync description with caption so preview reflects current user input
        // Use ?? (not ||) so an intentionally empty caption stays empty
        const embedCap = caption.value?.trim() ?? "";
        item.caption = embedCap;
        item.description = embedCap;
      }

      return item;
    });

    // The published viewer and final composer preview must resolve the same
    // canonical item. Keep ImageCard's description-first preview item intact
    // for dedicated formats while restoring per-media captions here.
    const presentationPreviewItem = computed(() => {
      const item = previewItem.value;
      const selected = selectedType.value;
      const isPlainTextDraft =
        !selected ||
        (selected === "poster" &&
          !poster.value.embedHtml &&
          !poster.value.url);
      const textualSubtypeBody = editingItemId.value
        ? caption.value
        : caption.value || description.value;

      return {
        ...item,
        mediaType:
          selected === "photos"
            ? "image"
            : isPlainTextDraft
            ? deriveTextualMediaType(
                title.value,
                textualSubtypeBody,
              )
            : item.mediaType,
        imageCaptions: [...imageCaptions.value],
      };
    });

    const previewPresentationMode = computed(() =>
      resolvePresentationMode(presentationPreviewItem.value),
    );
    const adaptivePresentationModes = new Set([
      PRESENTATION_MODES.STORY,
      PRESENTATION_MODES.GALLERY,
      PRESENTATION_MODES.ARTICLE,
    ]);
    const usesAdaptivePresentationPreview = computed(() =>
      adaptivePresentationModes.has(previewPresentationMode.value),
    );
    const previewPresentationLabel = computed(() => {
      const mode = previewPresentationMode.value;
      return mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : "";
    });

    // Always show the preview frame on open; Next is gated by hasPreview
    const showPreviewArea = computed(() => true);
    // Keep footer visible even when previewFocused; only hide for camera errors
    const hideInlineInputRow = computed(() => !!cameraError.value);
    const showFooter = computed(() => {
      // Keep footer visible in edit mode for all types, including video
      // Only hide if explicitly preview-focused or fatal camera error
      return !hideInlineInputRow.value;
    });

    // Title is editable via input; keep simple placeholder in the field

    watch(hasPreview, (v) => emit("update-canPreview", !!v), {
      immediate: true,
    });

    onMounted(() => {
      try {
        const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
        log("mounted", {
          ua,
          isMobile: isMobile(),
          hasMediaDevices: !!(navigator && navigator.mediaDevices),
          hasGetUserMedia: !!(
            navigator &&
            navigator.mediaDevices &&
            navigator.mediaDevices.getUserMedia
          ),
          isSecureContext:
            typeof window !== "undefined" ? window.isSecureContext : undefined,
          canUseCamera: canUseCamera(),
        });
      } catch {}
      try {
        log("refs at mount", {
          allInput: !!allInput.value,
          imageInput: !!imageInput.value,
          videoInput: !!videoInput.value,
          imageLibraryInput: !!imageLibraryInput.value,
          videoLibraryInput: !!videoLibraryInput.value,
        });
      } catch {}
      // Load recent emojis
      try {
        const saved = JSON.parse(
          localStorage.getItem("pc_recent_emojis") || "[]",
        );
        if (Array.isArray(saved)) recentEmojis.value = saved.slice(0, 20);
      } catch {}
      if (!isMobile())
        setTimeout(() => {
          // Focus title field on mount (desktop only)
          titleField.fieldRef.value?.focus();
        }, 0);
      try {
        const imgEl = imageInput.value;
        const vidEl = videoInput.value;
        log("fileInputs", {
          imageAccept: imgEl?.getAttribute("accept"),
          imageCapture: imgEl?.getAttribute("capture"),
          imageMultiple: !!imgEl?.multiple,
          videoAccept: vidEl?.getAttribute("accept"),
          videoCapture: vidEl?.getAttribute("capture"),
        });
      } catch {}

      // Listen for YouTube iframe postMessage errors
      window.addEventListener("message", handleYouTubeMessage);
    });

    // Hidden pickers
    const imageInput = ref(null);
    const imageLibraryInput = ref(null);
    const videoInput = ref(null);
    const videoLibraryInput = ref(null);
    const docInput = ref(null);
    const audioInput = ref(null);
    const allInput = ref(null);
    // Unique IDs per instance to avoid collisions
    const idSuffix = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const imageInputId = `pc_image_input_${idSuffix}`;
    const imageLibraryInputId = `pc_image_library_input_${idSuffix}`;
    const videoInputId = `pc_video_input_${idSuffix}`;
    const videoLibraryInputId = `pc_video_library_input_${idSuffix}`;
    const docInputId = `pc_doc_input_${idSuffix}`;
    const audioInputId = `pc_audio_input_${idSuffix}`;
    const audioCoverInputId = `pc_audio_cover_input_${idSuffix}`;
    const audioCoverInput = ref(null);
    const allInputId = `pc_all_input_${idSuffix}`;


	    const clearOtherTypes = (keep) => {
	      if (keep !== "photos") {
	        (images.value || []).forEach((item) => revokeVideoPosterPreview(item));
	        images.value = [];
	      }
	      if (keep !== "documents") documents.value = [];
	      if (keep !== "poster" && keep !== "embed")
	        poster.value = { url: "", embedHtml: "", meta: {} };
	      if (keep !== "video") {
	        if (videoFile.value) revokeVideoPosterPreview(videoFile.value);
	        if (existingVideoUrl.value)
	          revokeVideoPosterPreview(existingVideoUrl.value);
	        videoFile.value = null;
	        existingVideoUrl.value = "";
	      }
      if (keep !== "audio") {
        audioFile.value = null;
        audioCoverImage.value = null;
        existingAudioUrl.value = "";
      }
    };

    const chooseType = (type) => {
      log("chooseType", type);
      isMediaMenuOpen.value = false;
      if (type === "photos") {
        selectedType.value = "photos";
        clearOtherTypes("photos");
        log("chooseType photos (label will open input)", {
          hasEl: !!imageInput.value,
        });
      } else if (type === "video") {
        selectedType.value = "video";
        clearOtherTypes("video");
        log("chooseType video (label will open input)", {
          hasEl: !!videoInput.value,
        });
      } else if (type === "documents") {
        selectedType.value = "documents";
        clearOtherTypes("documents");
        log("chooseType documents (label will open input)", {
          hasEl: !!docInput.value,
        });
      } else if (type === "audio") {
        selectedType.value = "audio";
        clearOtherTypes("audio");
        log("chooseType audio (label will open input)", {
          hasEl: !!audioInput.value,
        });
      } else if (type === "poster") {
        selectedType.value = "poster";
        clearOtherTypes("poster");
        // Focus the poster URL input so users can paste immediately
        requestAnimationFrame(() => posterUrlInputRef.value?.focus());
      }
    };

    const activateMediaInput = (type, input) => {
      chooseType(type);
      input?.click();
    };

    // Pick handlers
	    const appendImages = (items) => {
	      try {
	        const unique = [];
	        const seen = new Set();
	        const all = [...(images.value || []), ...(items || [])];
	        for (const it of all) {
	          const key =
	            typeof it === "string"
	              ? it
	              : `${it?.name || "file"}_${it?.size || 0}_${it?.type || ""}_${it?.lastModified || ""}`;
	          if (seen.has(key)) continue;
	          seen.add(key);
	          unique.push(it);
	          if (unique.length >= 20) break;
	        }
	        images.value = unique;
	        unique.forEach((item) => {
	          if (isVideoMedia(item)) ensureVideoPosterPreview(item);
	        });
	        // If any images were added, ensure type is photos
	        updateSelectedTypeFromContent();
	      } catch (e) {
	        log("appendImages:error", e?.message || e);
	      }
    };
    const onPickImages = (e) => {
      const files = Array.from(e.target.files || []);
      log("onPickImages", {
        count: files.length,
        types: files.map((f) => f.type),
        selectedType: selectedType.value,
        mode: "append",
      });
      appendImages(files);
      // show tray immediately
      selectedType.value = "photos";
      previewFocused.value = false; // keep footer visible
      viewMode.value = "edit";
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };
    const setStandaloneVideo = (file) => {
      if (videoFile.value && videoFile.value !== file)
        revokeVideoPosterPreview(videoFile.value);
      videoFile.value = file || null;
      existingVideoUrl.value = "";
      if (file) ensureVideoPosterPreview(file);
      selectedType.value = "video";
    };
    const moveStandaloneVideoIntoMixedMedia = () => {
      const standalone = videoFile.value || existingVideoUrl.value;
      if (!standalone) return;
      videoFile.value = null;
      existingVideoUrl.value = "";
      appendImages([standalone]);
    };
    const onPickVideo = (e) => {
      const files = Array.from(e.target.files || []).filter((file) =>
        String(file?.type || "").startsWith("video/"),
      );
      log("onPickVideo", {
        count: files.length,
        types: files.map((f) => f.type),
        sizes: files.map((f) => f.size),
      });
      if (files.length === 1 && images.value.length === 0) {
        setStandaloneVideo(files[0]);
      } else if (files.length) {
        addMediaItems(files);
      }
      previewFocused.value = false;
      viewMode.value = "edit";
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };
    const onPickDocuments = (e) => {
      const files = Array.from(e.target.files || []).slice(0, 20);
      log("onPickDocuments", { count: files.length });
      documents.value = files.map((f, i) => ({
        id: `${Date.now()}_${i}`,
        name: f.name,
        url:
          typeof URL !== "undefined" && URL.createObjectURL
            ? URL.createObjectURL(f)
            : "",
        file: f,
        type: f.type || "",
      }));
      activeDocIndex.value = 0;
      // ensure the UI shows the inline tray for documents
      selectedType.value = "documents";
      previewFocused.value = false;
      viewMode.value = "edit";
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };
    const onPickAudio = (e) => {
      const file = (e.target.files || [])[0] || null;
      log("onPickAudio", file ? { type: file.type, size: file.size } : null);
      audioFile.value = file;
      existingAudioUrl.value = "";
      updateSelectedTypeFromContent();
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };

    const onAddAudioClick = () => {
      if (audioInput.value) audioInput.value.click();
    };


    const hasAudio = computed(
      () => !!audioFile.value || !!existingAudioUrl.value,
    );
    const clearAudio = () => {
      audioFile.value = null;
      audioCoverImage.value = null;
      existingAudioUrl.value = "";
    };
    const onPickAudioCover = (e) => {
      const file = (e.target.files || [])[0] || null;
      log("onPickAudioCover", file ? { type: file.type, size: file.size } : null);
      if (file) audioCoverImage.value = file;
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };
    const clearAudioCover = () => {
      audioCoverImage.value = null;
    };

    // Mixed add + picker
    const addMediaItems = (files = []) => {
      log("addMediaItems called", {
        count: files.length,
        types: files.map((f) => f.type),
      });
      const imgs = [];
      const vids = [];
      const auds = [];
      const docs = [];
      for (const f of files) {
        if (!f) continue;
        if (typeof f.type === "string" && f.type.startsWith("image/")) {
          imgs.push(f);
        } else if (typeof f.type === "string" && f.type.startsWith("video/")) {
          vids.push(f);
        } else if (typeof f.type === "string" && f.type.startsWith("audio/")) {
          auds.push(f);
        } else {
          docs.push(f);
        }
      }
      const hasStandaloneVideo = !!videoFile.value || !!existingVideoUrl.value;
      const hasVisualMedia = (images.value?.length || 0) > 0;
      const shouldUseMixedVideoLayout =
        vids.length > 1 ||
        imgs.length > 0 ||
        docs.length > 0 ||
        hasVisualMedia ||
        (hasStandaloneVideo && vids.length > 0);

      if ((imgs.length || (vids.length && shouldUseMixedVideoLayout)) && hasStandaloneVideo) {
        moveStandaloneVideoIntoMixedMedia();
      }

      if (imgs.length) appendImages(imgs);
      if (vids.length) {
        if (shouldUseMixedVideoLayout) {
          appendImages(vids);
        } else {
          setStandaloneVideo(vids[0]);
        }
      }
      if (auds.length) {
        audioFile.value = auds[auds.length - 1];
        existingAudioUrl.value = "";
        // If no video is present, switch to audio type, otherwise keep video (overlay mode)
        if (!vids.length && !imgs.length && !docs.length) {
          selectedType.value = "audio";
        }
      }
      if (docs.length) {
        const mapped = docs.slice(0, 20).map((f, i) => ({
          id: `${Date.now()}_${i}`,
          name: f.name || `file_${i + 1}`,
          url:
            typeof URL !== "undefined" && URL.createObjectURL
              ? URL.createObjectURL(f)
              : "",
          file: f,
          type: f.type || "",
        }));
        documents.value = [...(documents.value || []), ...mapped].slice(0, 20);
      }
      updateSelectedTypeFromContent();
      // NOTE: Do NOT auto-trigger open-sale here. User should see preview first,
      // then click forward arrow which will check isNftMintPending and open mint modal.
    };
    const onPickMixedFiles = (e) => {
      const files = Array.from(e.target.files || []);
      log("onPickMixedFiles", {
        count: files.length,
        types: files.map((f) => f.type),
      });
      addMediaItems(files);
      // ensure UI shows inline tray
      previewFocused.value = false;
      viewMode.value = "edit";
      // Clear input so re-selecting the same file triggers change again
      queueMicrotask(() => { if (e?.target) e.target.value = ""; });
    };

    // Poster link handling
    const onPosterInput = async () => {
      const url = posterInput.value?.trim();
      console.log("[PostComposer] onPosterInput called", { url });
      if (!url) {
        poster.value = { url: "", embedHtml: "", meta: {} };
        return;
      }
      const embed = await detectEmbed(url);
      console.log("[PostComposer] onPosterInput: detectEmbed result", embed);
      if (embed?.html) {
        poster.value = {
          url,
          embedHtml: sanitizeEmbedHtml(embed.html),
          embedProvider: embed.provider || null,
          thumbnailUrl: embed.thumbnailUrl || "",
          meta: embed.meta || {},
          embedIsVertical: embed.embedIsVertical || false,
        };
      } else {
        poster.value = { url, embedHtml: "", embedProvider: null, thumbnailUrl: "", meta: embed?.meta || {} };
      }
      console.log("[PostComposer] onPosterInput: poster.value set", JSON.parse(JSON.stringify(poster.value)));
    };

    // ImageUpload update handler
	    const onImagesUpdate = (updatedImages) => {
	      const unique = [];
	      const seen = new Set();
	      for (const it of updatedImages || []) {
	        const key =
	          typeof it === "string"
	            ? it
	            : `${it?.name || "file"}_${it?.size || 0}_${it?.type || ""}_${it?.lastModified || ""}`;
	        if (seen.has(key)) continue;
	        seen.add(key);
	        unique.push(it);
	        if (unique.length >= 20) break;
	      }
	      images.value = unique;
	      unique.forEach((item) => {
	        if (isVideoMedia(item)) ensureVideoPosterPreview(item);
	      });
	      updateSelectedTypeFromContent();
	    };

    // --- Camera composable ---
    const {
      captureMode,
      cameraError,
      cameraPreviewRef,
      cameraTileRef,
      isRecording,
      recordingProgressDeg,
      currentFacingMode,
      isMobile,
      canUseCamera,
      canShowCameraIcon,
      stopCamera,
      openLiveCamera,
      switchCamera,
      onShutterPressStart,
      onShutterPressEnd,
      onShutterPressCancel,
      openDevicePicker,
      onCameraTileClick,
      onCameraFooterClick,
    } = usePostComposerCamera({
      imageInput,
      imageLibraryInput,
      videoLibraryInput,
      selectedType,
      viewMode,
      addMediaItems,
      clearOtherTypes,
      openUploadPicker,
      log,
    });

    // Computed wrapper for canShowCameraIcon — avoids a function call
    // in a v-if that could produce unstable block children.
    const showCameraIcon = computed(() => canShowCameraIcon());

    onUnmounted(() => {
      window.removeEventListener("message", handleYouTubeMessage);
      try {
        const arr = Array.isArray(images.value) ? images.value : [];
        for (const it of arr)
          if (it && typeof it !== "string") revokeImageObjectUrl(it);
      } catch {}
	      try {
	        if (videoFile.value) revokeVideoObjectUrl(videoFile.value);
	      } catch {}
	      clearVideoPosterPreviews();
	      try {
	        const docs = Array.isArray(documents.value) ? documents.value : [];
        for (const d of docs) {
          if (d?.url && typeof d.url === "string" && d.url.startsWith("blob:"))
            URL.revokeObjectURL(d.url);
        }
      } catch {}
    });

    const thumbDragIndex = ref(null);
    const onThumbDragStart = (i) => (thumbDragIndex.value = i);
    const onThumbDrop = (i) => {
      const from = thumbDragIndex.value;
      if (from == null || from === i) return;
      const arr = images.value.slice();
      const [moved] = arr.splice(from, 1);
      arr.splice(i, 0, moved);
      images.value = arr;
      if (thumbnailIndex.value === from) thumbnailIndex.value = i;
      thumbDragIndex.value = null;
    };
    const imageObjectUrlMap = new WeakMap();
    const getImageObjectUrl = (file) => {
      try {
        let url = imageObjectUrlMap.get(file);
        if (!url) {
          url = URL.createObjectURL(file);
          imageObjectUrlMap.set(file, url);
        }
        return url;
      } catch {
        return "";
      }
    };
    const revokeImageObjectUrl = (file) => {
      try {
        const url = imageObjectUrlMap.get(file);
        if (url) {
          URL.revokeObjectURL(url);
          imageObjectUrlMap.delete(file);
        }
      } catch {}
    };
	    const thumbnailSrc = (img) => {
	      if (!img) return "";
	      if (isVideoMedia(img)) return videoPosterPreviewUrl(img);
	      return typeof img === "string" ? img : getImageObjectUrl(img);
	    };
	    const currentPreviewMedia = computed(() => {
	      const arr = Array.isArray(images.value) ? images.value : [];
	      if (!arr.length) return null;
	      const idx = Math.max(
	        0,
	        Math.min(Number(thumbnailIndex.value) || 0, arr.length - 1),
	      );
	      return arr[idx] || null;
	    });
    const onEditThumb = (i) => {
      // TODO: integrate cropper here in a future pass
      thumbnailIndex.value = i;
    };
    const onTrayReorder = (from, to) => {
      try {
        if (from === to) return;
        const arr = images.value.slice();
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        images.value = arr;
        // Make dropped image the active thumbnail for immediate feedback
        thumbnailIndex.value = to;
      } catch (e) {
        console.warn("onTrayReorder failed", e);
      }
    };

    const onFileInputClick = (kind) => {
      log("file input clicked", kind);
    };

    const backToEdit = () => {
      previewFocused.value = false;
      viewMode.value = "edit";
    };
    const removeImageAt = (i) => {
      try {
        const arr = images.value.slice();
        if (i < 0 || i >= arr.length) return;
	        const removed = arr.splice(i, 1)[0];
	        if (removed && typeof removed !== "string")
	          revokeImageObjectUrl(removed);
	        revokeVideoPosterPreview(removed);
	        images.value = arr;
        if (thumbnailIndex.value >= arr.length)
          thumbnailIndex.value = Math.max(0, arr.length - 1);
        // If user removed all images, flip to poster type automatically
        if (arr.length === 0) {
          updateSelectedTypeFromContent();
          // After branch switch (mediaRegion → textRegion), re-sync
          // contenteditable fields so description/title text survives
          nextTick(() => {
            titleField.sync();
            descField.sync();
          });
        }
      } catch {}
    };

    // --- Tray composable (must be after removeImageAt) ---
    const {
      hasVideoInTray,
      mediaItemsForTray,
      selectedIndexForTray,
      onTraySelect,
      removeMediaAt,
      onTrayEdit,
      onTrayReorderMixed,
    } = usePostComposerTray({
      videoFile,
      existingVideoUrl,
      images,
      documents,
      thumbnailIndex,
      activeDocIndex,
	      selectedType,
	      videoPreviewUrl,
	      thumbnailSrc,
	      mediaPreviewSrc,
	      mediaTypeForItem,
	      standaloneVideoPosterSrc,
	      removeImageAt,
      onEditThumb,
      onTrayReorder,
      updateSelectedTypeFromContent,
    });

    // Also react when the entire images array reference is replaced
    watch(
      images,
      (arr, oldArr) => {
        try {
          updateSelectedTypeFromContent();
          const prev = Array.isArray(oldArr) ? oldArr : [];
          const nextSet = new Set(
            (Array.isArray(arr) ? arr : []).filter(
              (it) => it && typeof it !== "string",
            ),
          );
	          for (const it of prev) {
	            if (it && typeof it !== "string" && !nextSet.has(it)) {
	              revokeImageObjectUrl(it);
	              revokeVideoPosterPreview(it);
	            }
	          }
	        } catch {}
	      },
      { deep: false },
    );

    // Revoke video object URL when replaced
    watch(
      () => videoFile.value,
	      (newFile, oldFile) => {
	        try {
	          if (oldFile && oldFile !== newFile) {
	            revokeVideoObjectUrl(oldFile);
	            if (!Array.isArray(images.value) || !images.value.includes(oldFile)) {
	              revokeVideoPosterPreview(oldFile);
	            }
	          }
	        } catch {}
	      },
    );

    // Revoke document blob URLs when removed from list
    watch(
      documents,
      (arr, oldArr) => {
        try {
          const prev = Array.isArray(oldArr) ? oldArr : [];
          const nextIds = new Set(
            (Array.isArray(arr) ? arr : []).map((d) => d && d.id),
          );
          for (const d of prev) {
            if (!d) continue;
            if (!nextIds.has(d.id)) {
              if (
                d.url &&
                typeof d.url === "string" &&
                d.url.startsWith("blob:")
              ) {
                URL.revokeObjectURL(d.url);
              }
            }
          }
        } catch {}
      },
      { deep: false },
    );

    // Public API
    const collectDraft = async () => {
      const groupId = generateGroupId();

      // Get first image for preview (as blob URL or existing URL)
      let previewImage = "";
      if (images.value.length > 0) {
        const firstImg = images.value[0];
        if (typeof firstImg === "string") {
          previewImage = firstImg;
        } else if (firstImg instanceof File || firstImg instanceof Blob) {
          previewImage = URL.createObjectURL(firstImg);
        }
      }

      // Sync imageCaptions to match images length
      syncImageCaptions();

      return {
        title: title.value.trim(),
        description: description.value.trim(),
        caption: caption.value.trim(), // Legacy compat
        imageCaptions: [...imageCaptions.value], // Per-image captions
        type: selectedType.value,
        selectedType: selectedType.value, // alias for compatibility
        groupId,
        images: images.value, // Include actual images array
        imagesCount: images.value.length,
        poster: poster.value,
        hasPreview: hasPreview.value,
        previewImage, // Blob URL or string URL for first image
      };
    };

    const dedupeUrls = (urls) => {
      if (!Array.isArray(urls)) return [];
      const seen = new Set();
      const out = [];
      for (const url of urls) {
        if (!url || typeof url !== "string") continue;
        if (seen.has(url)) continue;
        seen.add(url);
        out.push(url);
      }
      return out;
    };

    const looksVideoUrl = (url) =>
      /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url || "");

    const loadFromItem = (item = {}) => {
      resetDraft();

      // Track if we're editing an existing item (has id)
      editingItemId.value = item.id || item.docId || null;
      editingItemCollection.value =
        item.collection || item.collectionPath || "images";

      const rawTitle = item.itemName || item.title || "";
      // Load description (new field) or fallback to caption for backward compat
      // Only treat description as present if it has actual content (not empty string)
      const hasDescription =
        typeof item.description === "string" && item.description.trim().length > 0;
      const rawDescription = hasDescription
        ? item.description
        : item.itemDescription || item.caption || "";
      title.value = String(rawTitle || "");
      description.value = String(rawDescription || "");
      // Legacy caption field - keep for backward compat
      caption.value = String(item.caption || rawDescription || "");

      // Load per-image captions if available
      if (Array.isArray(item.imageCaptions)) {
        imageCaptions.value = [...item.imageCaptions];
      } else {
        imageCaptions.value = [];
      }

	      const mediaType = String(item.mediaType || "").toLowerCase();
	      const rawImages = Array.isArray(item.images) ? item.images : [];
	      const rawMediaTypes = Array.isArray(item.mediaTypes)
	        ? item.mediaTypes.map((type) => String(type || "").toLowerCase())
	        : [];
	      const rawMediaThumbnails = Array.isArray(item.mediaThumbnails)
	        ? item.mediaThumbnails
	        : [];
	      const rawMediaVariants = Array.isArray(item.mediaVariants)
	        ? item.mediaVariants
	        : [];
	      rawImages.forEach((url, index) => {
	        const variantMap =
	          rawMediaVariants[index] ||
	          (index === (item.thumbnailIndex ?? 0)
	            ? item.variants || item.imageVariants
	            : null);
	        if (
	          typeof url === "string" &&
	          variantMap &&
	          typeof variantMap === "object" &&
	          !Array.isArray(variantMap)
	        ) {
	          loadedMediaVariantsByUrl.set(url, variantMap);
	        }
	      });
	      let imageUrls = dedupeUrls(rawImages).filter(
	        (u) => typeof u === "string" && !u.startsWith("blob:"),
	      );
      const hasDocumentUrls =
        Array.isArray(item.documentUrls) && item.documentUrls.length > 0;
      if (imageUrls.length === 0 && !hasDocumentUrls) {
        const variantMap = item.variants || item.imageVariants;
        let variantUrl = "";
        if (variantMap && typeof variantMap === "object") {
          variantUrl =
            variantMap.md ||
            variantMap.lg ||
            variantMap.sm ||
            variantMap.xl ||
            Object.values(variantMap)[0] ||
            "";
        }
        if (typeof variantUrl !== "string") variantUrl = "";
        const fallbackThumb =
          typeof item.thumbnail === "string"
            ? item.thumbnail
            : typeof item.thumbnailUrl === "string"
            ? item.thumbnailUrl
            : typeof item.previewUrl === "string"
            ? item.previewUrl
            : typeof item.posterUrl === "string"
            ? item.posterUrl
            : "";
        const mediaFallback =
          mediaType !== "audio" &&
          typeof item.mediaUrl === "string" &&
          item.mediaUrl
            ? item.mediaUrl
            : "";
        imageUrls = dedupeUrls([
          fallbackThumb,
          variantUrl,
          mediaFallback,
        ]).filter((u) => typeof u === "string" && u && !u.startsWith("blob:"));
      }

	      const hasMixedVisualVideos =
	        mediaType !== "video" &&
	        imageUrls.length > 1 &&
	        (rawMediaTypes.some((type) => type === "video") ||
	          imageUrls.some((u) => looksVideoUrl(u)));

	      const videoCandidates = [
	        item.mediaUrl,
	        item.videoUrl,
	        item.processedVideoUrl,
	        item.originalVideoUrl,
	        ...imageUrls,
	      ].filter((u) => typeof u === "string" && u);
	      const resolvedVideoUrl =
	        hasMixedVisualVideos
	          ? ""
	          : videoCandidates.find((u) => looksVideoUrl(u)) ||
	            (mediaType === "video" ? videoCandidates[0] : "");

      const resolvedAudioUrl =
        typeof item.audioUrl === "string"
          ? item.audioUrl
          : mediaType === "audio" && typeof item.mediaUrl === "string"
          ? item.mediaUrl
          : "";

      const documentUrls = Array.isArray(item.documentUrls)
        ? dedupeUrls(item.documentUrls)
        : [];
      const documentMetadata = Array.isArray(item.documentMetadata)
        ? item.documentMetadata
        : [];

      const hasDocuments =
        mediaType === "documents" ||
        (documentUrls.length > 0 && !resolvedVideoUrl && !resolvedAudioUrl);

	      if (resolvedVideoUrl) {
	        existingVideoUrl.value = resolvedVideoUrl;
	        const posterUrl = rawMediaThumbnails.find(Boolean);
	        if (posterUrl) setVideoPosterPreviewForUrl(resolvedVideoUrl, posterUrl);
	        // Filter out the video URL, any video-looking URLs, and poster URLs from mediaThumbnails
	        const posterUrls = new Set(
	          rawMediaThumbnails.filter(Boolean)
	        );
	        images.value = imageUrls.filter(
	          (u) => u !== resolvedVideoUrl && !looksVideoUrl(u) && !posterUrls.has(u),
	        );
	      } else if (resolvedAudioUrl || mediaType === "audio") {
	        existingAudioUrl.value = resolvedAudioUrl || "";
	        images.value = imageUrls;
	      } else if (hasDocuments) {
	        images.value = imageUrls;
	      } else {
	        images.value = imageUrls;
	      }

	      if (!resolvedVideoUrl && imageUrls.length && rawMediaThumbnails.length) {
	        imageUrls.forEach((url, idx) => {
	          const type = rawMediaTypes[idx] || (looksVideoUrl(url) ? "video" : "");
	          if (type === "video" && rawMediaThumbnails[idx]) {
	            setVideoPosterPreviewForUrl(url, rawMediaThumbnails[idx]);
	          }
	        });
	      }

      if (hasDocuments && documentUrls.length) {
        documents.value = documentUrls.map((url, idx) => {
          const meta = documentMetadata[idx] || {};
          let fallbackName = "Document";
          if (typeof url === "string") {
            const rawName = url.split("/").pop()?.split("?")[0] || "Document";
            try {
              fallbackName = decodeURIComponent(rawName);
            } catch {
              fallbackName = rawName;
            }
          }
          const name = meta?.name || fallbackName;
          return {
            id: `${Date.now()}_${idx}`,
            name,
            url,
            type: meta?.type || "",
          };
        });
        activeDocIndex.value = 0;
      }

      const maxThumb = Math.max(0, (images.value?.length || 1) - 1);
      const rawThumb = Number(item.thumbnailIndex) || 0;
      thumbnailIndex.value = Math.max(0, Math.min(rawThumb, maxThumb));

      // Detect if editing an existing minted NFT (has contract address, token ID, or mintStatus)
      const hasMintedNFT = Boolean(
        item.contractAddress ||
          item.tokenId ||
          item.mintStatus === "minted" ||
          (item.nft && (item.nft.contractAddress || item.nft.tokenId)),
      );
      isEditingMintedNFT.value = hasMintedNFT;

      // Hydrate poster/embed data if present (for editing embed posts)
      if (item.embedHtml || item.embedUrl) {
        poster.value = {
          url: item.embedUrl || "",
          embedHtml: item.embedHtml || "",
          embedProvider: item.embedProvider || null,
          thumbnailUrl: item.thumbnailUrl || "",
          meta: item.embedMeta || {},
          embedIsVertical: item.embedIsVertical || false,
        };
        posterInput.value = item.embedUrl || "";
      }

      previewFocused.value = false;
      viewMode.value = "edit";
      updateSelectedTypeFromContent();

      // Push loaded values into contenteditable DOM
      nextTick(() => {
        titleField.sync();
        descField.sync();
        captionField.sync();
        imageCaptionField.sync();
      });
    };

    const save = async ({ owner, organisation, uploadId } = {}) => {
      const stableCreationOperationId = ensureCreationOperationId();
      const reportProgress = (progress) => {
        if (!uploadId) return;
        store.dispatch("uploadQueue/updateProgress", {
          id: uploadId,
          progress: Math.max(0, Math.min(100, Math.round(progress))),
        });
      };

      const createByteProgressAggregator = (files) => {
        if (!uploadId) return null;
        const normalizedFiles = (files || []).filter(Boolean);
        const sizes = normalizedFiles.map((f) =>
          typeof f?.size === "number" ? f.size : 0,
        );
        const totalBytes = sizes.reduce((sum, n) => sum + n, 0) || 1;
        const transferred = new Array(normalizedFiles.length).fill(0);

        const report = () => {
          const done = transferred.reduce((sum, n) => sum + n, 0);
          reportProgress((done / totalBytes) * 100);
        };

        return {
          set: (index, percent) => {
            const p = Math.max(0, Math.min(100, Number(percent) || 0));
            transferred[index] = Math.round((sizes[index] || 0) * (p / 100));
            report();
          },
          done: (index) => {
            transferred[index] = sizes[index] || 0;
            report();
          },
        };
      };

      // Global safeguard: require at least some content (media OR title OR caption)
      try {
        const noImages =
          !Array.isArray(images.value) || images.value.length === 0;
        const noDocs =
          !Array.isArray(documents.value) || documents.value.length === 0;
        const noPoster = !poster.value?.url && !poster.value?.embedHtml;
        const noVideo = !videoFile.value;
        const noAudio = !audioFile.value;
        const noMedia = noImages && noDocs && noPoster && noVideo && noAudio;
        const noText =
          !title.value?.trim() &&
          !caption.value?.trim() &&
          !description.value?.trim();
        if (noMedia && noText) {
          throw new Error(
            "Add an image, a title, or a caption before publishing.",
          );
        }
      } catch (e) {
        // Re-throw so caller (SellingAction) can surface toast
        throw e;
      }
      // Allow text-only posts (no media)
      if (!selectedType.value) {
        if (
          !title.value.trim() &&
          !caption.value.trim() &&
          !description.value.trim()
        )
          throw new Error("Nothing to post");
        const res = await saveTextualPost({
          title: title.value,
          caption: caption.value || description.value,
          description: description.value,
          owner,
          organisation,
          store,
          creationOperationId: stableCreationOperationId,
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          // Notify timeline-based views to refresh
          try {
            window.dispatchEvent(
              new CustomEvent("marketplace:new-media", {
                detail: { owner, organisation, kind: "text" },
              }),
            );
          } catch {}
        } catch {}
        try {
          emit("saved", { mode: "create", type: "text", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      if (selectedType.value === "photos") {
        // Sync imageCaptions before saving
        syncImageCaptions();

        // Generate per-video poster thumbnails for any video files in the mix
        const mediaThumbnails = new Array(images.value.length).fill("");
        const mediaTypes = images.value.map((item) => mediaTypeForItem(item));
        const isVideoFile = (item) => {
          if (item instanceof File || item instanceof Blob) {
            return (item.type || "").startsWith("video/");
          }
          if (typeof item === "string") {
            return /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(item);
          }
          return false;
        };
        for (let idx = 0; idx < images.value.length; idx++) {
          const item = images.value[idx];
          if (typeof item === "string") {
            const existingPoster = videoPosterPreviewUrl(item);
            if (existingPoster && !existingPoster.startsWith("blob:")) {
              mediaThumbnails[idx] = existingPoster;
            }
            continue;
          }
          if (isVideoFile(item) && item instanceof File) {
            try {
              const { generateVideoPoster } = await import(
                "@/utils/uploadUtils.js"
              );
              const res = await generateVideoPoster(item, {
                folder: "images",
                ownerUid: owner,
                atTimeSec: 0.2,
              });
              if (res?.url) mediaThumbnails[idx] = res.url;
            } catch (e) {
              console.warn("[PostComposer] Failed to generate poster for video at index", idx, e);
            }
          }
        }
        const hasVideoPosters = mediaThumbnails.some(Boolean);

        const res = uploadId
          ? await savePhotosPostWithProgress({
              title: title.value,
              caption: caption.value,
              description: description.value,
              imageCaptions: [...imageCaptions.value],
              ...(hasVideoPosters ? { mediaThumbnails } : {}),
              mediaTypes,
              filesOrUrls: images.value,
              thumbnailIndex: thumbnailIndex.value,
              owner,
              organisation,
              store,
              uploadId,
              creationOperationId: stableCreationOperationId,
            })
          : await savePhotosPost({
              title: title.value,
              caption: caption.value,
              description: description.value,
              imageCaptions: [...imageCaptions.value],
              ...(hasVideoPosters ? { mediaThumbnails } : {}),
              mediaTypes,
              filesOrUrls: images.value,
              thumbnailIndex: thumbnailIndex.value,
              owner,
              organisation,
              store,
              creationOperationId: stableCreationOperationId,
            });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          // Notify timeline-based views to refresh for newly created media
          try {
            window.dispatchEvent(
              new CustomEvent("marketplace:new-media", {
                detail: { owner, organisation, kind: "image" },
              }),
            );
          } catch {}
        } catch {}
        try {
          emit("saved", { mode: "create", type: "image", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      if (selectedType.value === "video" && videoFile.value) {
        // Upload video then create a video media post
        let videoUrl = "";
        const progressAgg = uploadId
          ? createByteProgressAggregator(
              audioFile.value && process.env.VUE_APP_VIDEO_RENDER_URL
                ? [videoFile.value, audioFile.value]
                : [videoFile.value],
            )
          : null;
        try {
          videoUrl = await store.dispatch("mediaActions/uploadMediaFile", {
            file: videoFile.value,
            type: "video",
            owner,
            organisation,
            creationOperationId: stableCreationOperationId,
            onProgress: (p) => progressAgg?.set(0, p),
          });
          progressAgg?.done(0);
	        } catch (e) {
	          console.warn("[PostComposer] Video upload failed:", e);
	          throw e;
	        }
	        const originalVideoUrl = videoUrl;
	        let videoDeliveryProfile = "original";

	        // Optimize through the renderer when available. With audio this creates
	        // the composite video; without audio it remuxes to fast-start MP4.
	        if (process.env.VUE_APP_VIDEO_RENDER_URL) {
	          let audioUrl = null;
	          try {
	            isSaving.value = true;
	            if (audioFile.value) {
	              audioUrl = await store.dispatch("mediaActions/uploadMediaFile", {
	                file: audioFile.value,
	                type: "audio",
	                owner,
	                organisation,
	                creationOperationId: stableCreationOperationId,
	                onProgress: (p) => progressAgg?.set(1, p),
	              });
	              progressAgg?.done(1);
	            }

	            // Call Render Service
	            const renderRes = await fetch(
	              `${process.env.VUE_APP_VIDEO_RENDER_URL}/render`,
	              {
	                method: "POST",
	                headers: { "Content-Type": "application/json" },
	                body: JSON.stringify(
	                  audioUrl
	                    ? {
	                        videoUrl,
	                        profile: "web-v1",
	                        audio: {
	                          url: audioUrl,
	                          startTime: 0, // Default to 0 for now (can add UI later)
	                          volume: 1.0, // Default to 100%
	                          keepOriginalAudio: true,
	                        },
	                      }
	                    : { videoUrl, profile: "web-v1" },
	                ),
	              },
	            );

            if (!renderRes.ok) {
              throw new Error(`Render failed: ${renderRes.statusText}`);
            }

            const renderData = await renderRes.json();
            if (renderData.outputUrl) {
              // If we have a new composite video, the original video and audio are now intermediate files
              // We should mark them for deletion or delete them now?
              // Actually, let's keep them for now as "source" or delete them if we want to save space.
              // For now, let's just update the videoUrl to the new composite one.
              // The original videoUrl and audioUrl will be orphaned if we don't delete them here.
              // But maybe we want to keep the original video?
              // The prompt asked to clean up "if the posting fails".
              // So we only focus on the catch block for now.

              // OPTIONAL: Delete the separate audio file since it's now baked in?
              // store.dispatch("mediaActions/deleteImagesFromStorage", [audioUrl]);

              videoUrl = renderData.outputUrl; // Override with composite URL
              videoDeliveryProfile = renderData.profile || "web-v1";
            }
	          } catch (err) {
	            console.error("[PostComposer] Video render failed:", err);

	            // CLEANUP: Delete uploaded files to prevent storage leaks
	            const filesToDelete = [];
	            if (audioFile.value && videoUrl) filesToDelete.push(videoUrl);
	            if (audioUrl) filesToDelete.push(audioUrl);

	            if (filesToDelete.length > 0) {
              log(
                "Cleaning up orphaned files:",
                filesToDelete,
              );
              // Use deleteImagesFromStorage which handles generic file URLs
              store
                .dispatch("mediaActions/deleteImagesFromStorage", filesToDelete)
                .catch((e) =>
                  console.warn("[PostComposer] Cleanup failed:", e),
                );
            }

	            if (audioFile.value) {
	              throw new Error(
	                "Could not render video with music. Please try again or remove the audio.",
	              );
	            }
	            console.warn("[PostComposer] Continuing with raw uploaded video.");
	          } finally {
	            isSaving.value = false;
	          }
        }

        const customThumbnailUrls = [];
        const customThumbnailVariants = [];
        const addCustomThumbnail = (url, variants = null) => {
          if (!url || customThumbnailUrls.includes(url)) return;
          customThumbnailUrls.push(url);
          customThumbnailVariants.push(variants);
        };
        for (const candidate of Array.isArray(images.value) ? images.value : []) {
          if (!candidate) continue;
          if (typeof candidate === "string") {
            if (!candidate.startsWith("blob:")) {
              addCustomThumbnail(
                candidate,
                loadedMediaVariantsByUrl.get(candidate) || null,
              );
            }
            continue;
          }
          try {
            const { uploadImageWithVariants } = await import(
              "@/utils/uploadUtils.js"
            );
            const uploadedThumb = await uploadImageWithVariants(candidate, {
              folder: "images",
              ownerUid: owner,
              widths: [256, 512, 1024, 1920],
            });
            addCustomThumbnail(uploadedThumb?.url, uploadedThumb?.variants);
          } catch (e) {
            console.warn("[PostComposer] Failed to upload custom video thumbnail:", e);
          }
        }

        // Generate and upload a poster image for the video for fast thumbnails
        let posterUrl = null;
        try {
          const { generateVideoPoster } = await import(
            "@/utils/uploadUtils.js"
          );
          const res = await generateVideoPoster(videoFile.value, {
            folder: "images",
            ownerUid: owner,
            atTimeSec: 0.2,
          });
          posterUrl = res?.url || null;
        } catch (e) {
          console.warn("[PostComposer] Failed to generate video poster:", e);
        }

        // Sync imageCaptions before saving
        syncImageCaptions();

        const payload = {
          itemName: title.value.trim(),
          caption: caption.value.trim(),
          description: description.value.trim(),
          imageCaptions: [...imageCaptions.value],
	          mediaType: "video",
	          mediaUrl: videoUrl,
	          processedVideoUrl: videoUrl, // Set canonical processed video URL
	          videoDeliveryProfile,
	          ...(originalVideoUrl && originalVideoUrl !== videoUrl
	            ? { originalVideoUrl }
	            : {}),
          // images[] should only contain user-provided images (custom thumbnails),
          // NOT the generated poster or the video URL itself.
          // The video URL lives in mediaUrl; the poster lives in mediaThumbnails.
          images: customThumbnailUrls,
          mediaVariants: customThumbnailVariants,
          ...(customThumbnailVariants[0]
            ? { variants: customThumbnailVariants[0] }
            : {}),
          ...(posterUrl ? { mediaThumbnails: [posterUrl] } : {}),
          owner,
          organisation,
          thumbnailIndex: 0,
          creationOperationId: stableCreationOperationId,
        };
        const res = await store.dispatch("mediaActions/addMediaPost", payload);
        reportProgress(100);
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          window.dispatchEvent(
            new CustomEvent("marketplace:new-media", {
              detail: { owner, organisation, kind: "video" },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "create", type: "video", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      if (selectedType.value === "documents" && documents.value.length > 0) {
        // Upload documents and create a documents media post
        const uploadedDocs = [];
        const docFiles = documents.value.map((d) => d?.file).filter(Boolean);
        const docsProgressAgg = createByteProgressAggregator(docFiles);
        try {
          // Upload each document file to Firebase Storage
          let fileIndex = 0;
          for (const doc of documents.value) {
            if (doc.file) {
              const url = await store.dispatch("mediaActions/uploadMediaFile", {
                file: doc.file,
                type: "document",
                owner,
                organisation,
                creationOperationId: stableCreationOperationId,
                onProgress: (p) => docsProgressAgg?.set(fileIndex, p),
              });
              docsProgressAgg?.done(fileIndex);
              fileIndex++;
              uploadedDocs.push({
                name: doc.name,
                url: url,
                type: doc.type || "application/octet-stream",
              });
            } else if (doc.url) {
              // Already has a URL (shouldn't happen in create, but handle it)
              uploadedDocs.push(doc);
            }
          }
        } catch (e) {
          console.warn("[PostComposer] Document upload failed:", e);
          throw e;
        }

        if (uploadedDocs.length === 0) {
          throw new Error("No documents to upload");
        }

        // Store document URLs in dedicated documentUrls field (not images array)
        const documentUrls = uploadedDocs.map((d) => d.url);

        // Sync imageCaptions before saving
        syncImageCaptions();

        const payload = {
          itemName: title.value.trim() || uploadedDocs[0].name, // Use first doc name as fallback title
          caption: caption.value.trim(),
          description: description.value.trim(),
          imageCaptions: [...imageCaptions.value],
          mediaType: "documents",
          // Store document URLs in dedicated field (semantically correct)
          documentUrls: documentUrls,
          // Store document metadata in a separate field for reference
          documentMetadata: uploadedDocs.map((d) => ({
            name: d.name,
            type: d.type,
          })),
          owner,
          organisation,
          thumbnailIndex: 0,
          creationOperationId: stableCreationOperationId,
        };

        const res = await store.dispatch("mediaActions/addMediaPost", payload);
        reportProgress(100);
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          window.dispatchEvent(
            new CustomEvent("marketplace:new-media", {
              detail: { owner, organisation, kind: "documents" },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "create", type: "documents", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      if (selectedType.value === "audio" && audioFile.value) {
        // Use saveAudioPost service (supports cover image upload)
        const { saveAudioPost } = await import("@/services/postComposerService.js");
        const res = await saveAudioPost({
          title: title.value.trim(),
          caption: caption.value.trim(),
          description: description.value.trim(),
          audioFile: audioFile.value,
          coverImage: audioCoverImage.value || null,
          owner,
          organisation,
          store,
          uploadId,
          creationOperationId: stableCreationOperationId,
        });
        reportProgress(100);
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          window.dispatchEvent(
            new CustomEvent("marketplace:new-media", {
              detail: { owner, organisation, kind: "audio" },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "create", type: "audio", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      // Poster / embed posts (YouTube, SoundCloud links)
      if (selectedType.value === "embed") {
        console.log("[PostComposer] save: embed branch", {
          title: title.value,
          caption: caption.value,
          description: description.value,
          embedHtml: poster.value.embedHtml?.substring(0, 80),
          embedUrl: poster.value.url,
          embedProvider: poster.value.embedProvider,
        });
        if (
          !title.value.trim() &&
          !caption.value.trim() &&
          !description.value.trim() &&
          !poster.value.embedHtml
        )
          throw new Error("Nothing to post");

        // Keep title as-is — if user left the pasted URL it serves as fallback title
        // Sync caption ↔ description so resolveCardText (prefers description) shows latest
        const embedCap = (caption.value || description.value || "").trim();
        const res = await saveTextualPost({
          title: title.value,
          caption: embedCap,
          description: embedCap,
          owner,
          organisation,
          store,
          // Embed fields
          embedHtml: poster.value.embedHtml || "",
          embedUrl: poster.value.url || "",
          embedProvider: poster.value.embedProvider || null,
          thumbnailUrl: poster.value.thumbnailUrl || "",
          embedIsVertical: poster.value.embedIsVertical || false,
          embedIsSquare: poster.value.embedIsSquare || false,
          creationOperationId: stableCreationOperationId,
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
          try {
            window.dispatchEvent(
              new CustomEvent("marketplace:new-media", {
                detail: { owner, organisation, kind: "embed" },
              }),
            );
          } catch {}
        } catch {}
        try {
          emit("saved", { mode: "create", type: "embed", ...res });
        } catch {}
        resetDraft();
        return res;
      }
      throw new Error(`Save not implemented for type: ${selectedType.value}`);
    };

    // Save edits to an existing media item
    const saveEdit = async ({
      id,
      collection = "images",
      existingUrls = [],
      owner,
      organisation,
      originalPath,
      uploadId,
    } = {}) => {
      if (!id) throw new Error("Missing id for saveEdit");

      const resolvedOriginalPath =
        originalPath || (collection ? `${collection}/${id}` : "");
      const itemName = title.value.trim();
      const captionText = caption.value.trim();

      const createByteProgressAggregator = (files) => {
        const normalizedFiles = (files || []).filter(Boolean);
        const sizes = normalizedFiles.map((file) =>
          typeof file?.size === "number" ? file.size : 0,
        );
        const totalBytes = sizes.reduce((sum, size) => sum + size, 0) || 1;
        const transferred = new Array(normalizedFiles.length).fill(0);
        const report = () => {
          const done = transferred.reduce((sum, size) => sum + size, 0);
          store.dispatch("uploadQueue/updateProgress", {
            id: uploadId,
            progress: Math.max(
              0,
              Math.min(100, Math.round((done / totalBytes) * 100)),
            ),
          });
        };
        if (!uploadId) return null;
        return {
          set(index, percent) {
            const bounded = Math.max(0, Math.min(100, Number(percent) || 0));
            transferred[index] = Math.round((sizes[index] || 0) * (bounded / 100));
            report();
          },
          done(index) {
            transferred[index] = sizes[index] || 0;
            report();
          },
        };
      };

      const normalizeImageUrls = async ({
        excludeUrl,
        excludeVideos = false,
        includeVariants = false,
      } = {}) => {
        const preserved = [];
        const uploads = [];
        const items = Array.isArray(images.value) ? images.value : [];

        for (const it of items) {
          if (typeof it === "string") preserved.push(it);
          else if (it instanceof File || it instanceof Blob) uploads.push(it);
        }

        const uploaded = [];
        for (const file of uploads) {
          try {
            let url = "";
            if (String(file?.type || "").startsWith("image/")) {
              const { uploadImageWithVariants } = await import(
                "@/utils/uploadUtils.js"
              );
              const result = await uploadImageWithVariants(file, {
                folder: "images",
                ownerUid: owner,
                widths: [256, 512, 1024, 1920],
              });
              url = result?.url || "";
              if (url && result?.variants) {
                loadedMediaVariantsByUrl.set(url, result.variants);
              }
            } else {
              url = await store.dispatch("mediaActions/uploadMediaFile", {
                file,
                type: "image",
                owner,
                organisation,
                mediaId: id,
              });
            }
            if (url) uploaded.push(url);
          } catch (e) {
            console.warn("[PostComposer] Image upload failed on edit:", e);
          }
        }

        let urls = dedupeUrls([
          ...(Array.isArray(existingUrls) ? existingUrls : []),
          ...preserved,
          ...uploaded,
        ]).filter((u) => u && typeof u === "string" && !u.startsWith("blob:"));

        if (excludeUrl) {
          urls = urls.filter((u) => u !== excludeUrl);
        }
        if (excludeVideos) {
          urls = urls.filter((u) => !looksVideoUrl(u));
        }

        if (!includeVariants) return urls;
        return {
          urls,
          mediaVariants: urls.map(
            (url) => loadedMediaVariantsByUrl.get(url) || null,
          ),
        };
      };

      const imageDeliveryFields = (delivery, selectedIndex = 0) => {
        const urls = Array.isArray(delivery?.urls) ? delivery.urls : [];
        const mediaVariants = Array.isArray(delivery?.mediaVariants)
          ? delivery.mediaVariants
          : urls.map(() => null);
        return {
          images: urls,
          mediaVariants,
          // An empty map deliberately clears a stale legacy primary variant
          // when the selected source no longer has derivatives.
          variants: mediaVariants[selectedIndex] || {},
        };
      };

      if (videoFile.value || existingVideoUrl.value) {
        let videoUrl = existingVideoUrl.value || "";
        let originalVideoUrl = "";
        let videoDeliveryProfile = "";
        if (videoFile.value) {
          try {
            videoUrl = await store.dispatch("mediaActions/uploadMediaFile", {
              file: videoFile.value,
              type: "video",
              owner,
              organisation,
              mediaId: id,
            });
          } catch (e) {
            console.warn("[PostComposer] Failed to upload video on edit:", e);
            throw e;
          }
          originalVideoUrl = videoUrl;
          videoDeliveryProfile = "original";

          if (process.env.VUE_APP_VIDEO_RENDER_URL) {
            try {
              const renderResponse = await fetch(
                `${process.env.VUE_APP_VIDEO_RENDER_URL}/render`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ videoUrl, profile: "web-v1" }),
                },
              );
              if (!renderResponse.ok) {
                throw new Error(
                  `Render failed: ${renderResponse.statusText}`,
                );
              }
              const renderData = await renderResponse.json();
              if (renderData.outputUrl) {
                videoUrl = renderData.outputUrl;
                videoDeliveryProfile = renderData.profile || "web-v1";
              }
            } catch (error) {
              console.warn(
                "[PostComposer] Video optimization failed on edit; preserving the uploaded source:",
                error,
              );
            }
          }
        }

        const imageDelivery = await normalizeImageUrls({
          excludeUrl: videoUrl,
          excludeVideos: true,
          includeVariants: true,
        });
        const imageUrls = imageDelivery.urls;
        const clampedThumb = imageUrls.length
          ? Math.max(
              0,
              Math.min(Number(thumbnailIndex.value) || 0, imageUrls.length - 1),
            )
          : 0;

        // Sync imageCaptions to match images length
        syncImageCaptions();

        // Generate poster thumbnail for the video
        let mediaThumbnails = null;
        if (videoFile.value && videoUrl) {
          try {
            const { generateVideoPoster } = await import("@/utils/uploadUtils.js");
            const posterRes = await generateVideoPoster(videoFile.value, {
              folder: "images",
              ownerUid: owner,
              atTimeSec: 0.2,
            });
            if (posterRes?.url) {
              mediaThumbnails = [posterRes.url];
            }
          } catch (e) {
            console.warn("[PostComposer] Failed to generate video poster on edit:", e);
          }
        }

        const data = {
          itemName,
          caption: captionText, // Legacy compat
          description: description.value.trim(), // Whole-post description
          imageCaptions: [...imageCaptions.value], // Per-image captions
          mediaUrl: videoUrl || null,
          ...(videoFile.value
            ? {
                processedVideoUrl: videoUrl || null,
                videoDeliveryProfile,
                ...(originalVideoUrl && originalVideoUrl !== videoUrl
                  ? { originalVideoUrl }
                  : {}),
              }
            : {}),
          ...imageDeliveryFields(imageDelivery, clampedThumb),
          owner,
          organisation,
          mediaType: "video",
          type: "media",
          thumbnailIndex: clampedThumb,
          ...(mediaThumbnails ? { mediaThumbnails } : {}),
        };

        const res = await store.dispatch("mediaActions/updateMediaItem", {
          id,
          collection,
          data,
          existingUrls: data.images || [],
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
        } catch {}
        try {
          window.dispatchEvent(
            new CustomEvent("marketplace:item-updated", {
              detail: {
                originalPath: resolvedOriginalPath,
                itemName: data.itemName,
                itemDescription: data.caption,
                description: data.description || "",
                caption: data.caption || "",
                imageCaptions: data.imageCaptions || [],
                images: data.images || [],
                mediaVariants: data.mediaVariants || [],
                variants: data.variants || {},
                mediaThumbnails: data.mediaThumbnails || [],
                thumbnailIndex: data.thumbnailIndex || 0,
                mediaType: "video",
                mediaUrl: data.mediaUrl || "",
              },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "update", id, collection, ...res });
        } catch {}
        resetDraft();
        return res;
      }

      if (audioFile.value || existingAudioUrl.value) {
        let audioUrl = existingAudioUrl.value || "";
        if (audioFile.value) {
          try {
            audioUrl = await store.dispatch("mediaActions/uploadMediaFile", {
              file: audioFile.value,
              type: "audio",
              owner,
              organisation,
              mediaId: id,
            });
          } catch (e) {
            console.warn("[PostComposer] Failed to upload audio on edit:", e);
            throw e;
          }
        }

        const imageDelivery = await normalizeImageUrls({
          includeVariants: true,
        });
        const imageUrls = imageDelivery.urls;
        const clampedThumb = imageUrls.length
          ? Math.max(
              0,
              Math.min(Number(thumbnailIndex.value) || 0, imageUrls.length - 1),
            )
          : 0;

        // Sync imageCaptions to match images length
        syncImageCaptions();

        const data = {
          itemName,
          caption: captionText, // Legacy compat
          description: description.value.trim(), // Whole-post description
          imageCaptions: [...imageCaptions.value], // Per-image captions
          audioUrl: audioUrl || null,
          ...imageDeliveryFields(imageDelivery, clampedThumb),
          owner,
          organisation,
          mediaType: "audio",
          type: "media",
          thumbnailIndex: clampedThumb,
        };

        const res = await store.dispatch("mediaActions/updateMediaItem", {
          id,
          collection,
          data,
          existingUrls: data.images || [],
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
        } catch {}
        try {
          window.dispatchEvent(
            new CustomEvent("marketplace:item-updated", {
              detail: {
                originalPath: resolvedOriginalPath,
                itemName: data.itemName,
                itemDescription: data.caption,
                description: data.description || "",
                caption: data.caption || "",
                imageCaptions: data.imageCaptions || [],
                images: data.images || [],
                mediaVariants: data.mediaVariants || [],
                variants: data.variants || {},
                thumbnailIndex: data.thumbnailIndex || 0,
                mediaType: "audio",
                audioUrl: data.audioUrl || "",
              },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "update", id, collection, ...res });
        } catch {}
        resetDraft();
        return res;
      }

      if (selectedType.value === "documents" || documents.value.length > 0) {
        const uploadedDocs = [];
        const docFiles = documents.value.map((d) => d?.file).filter(Boolean);
        const docsProgressAgg = createByteProgressAggregator(docFiles);
        try {
          let fileIndex = 0;
          for (const doc of documents.value) {
            if (doc.file) {
              const url = await store.dispatch("mediaActions/uploadMediaFile", {
                file: doc.file,
                type: "document",
                owner,
                organisation,
                mediaId: id,
                onProgress: (p) => docsProgressAgg?.set(fileIndex, p),
              });
              docsProgressAgg?.done(fileIndex);
              fileIndex++;
              uploadedDocs.push({
                name: doc.name,
                url,
                type: doc.type || "application/octet-stream",
              });
            } else if (doc.url) {
              uploadedDocs.push({
                name: doc.name,
                url: doc.url,
                type: doc.type || "application/octet-stream",
              });
            }
          }
        } catch (e) {
          console.warn("[PostComposer] Document upload failed:", e);
          throw e;
        }

        if (uploadedDocs.length === 0) {
          throw new Error("No documents to upload");
        }

        const documentUrls = uploadedDocs.map((d) => d.url);
        const imageDelivery = await normalizeImageUrls({
          includeVariants: true,
        });

        // Sync imageCaptions to match images length
        syncImageCaptions();

        const data = {
          itemName: itemName || uploadedDocs[0].name,
          caption: captionText, // Legacy compat
          description: description.value.trim(), // Whole-post description
          imageCaptions: [...imageCaptions.value], // Per-image captions
          mediaType: "documents",
          type: "media",
          documentUrls,
          documentMetadata: uploadedDocs.map((d) => ({
            name: d.name,
            type: d.type,
          })),
          ...imageDeliveryFields(imageDelivery, 0),
          owner,
          organisation,
          thumbnailIndex: 0,
        };

        const res = await store.dispatch("mediaActions/updateMediaItem", {
          id,
          collection,
          data,
          existingUrls: data.images || [],
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
        } catch {}
        try {
          window.dispatchEvent(
            new CustomEvent("marketplace:item-updated", {
              detail: {
                originalPath: resolvedOriginalPath,
                itemName: data.itemName,
                itemDescription: data.caption,
                description: data.description || "",
                caption: data.caption || "",
                imageCaptions: data.imageCaptions || [],
                images: data.images || [],
                mediaVariants: data.mediaVariants || [],
                variants: data.variants || {},
                thumbnailIndex: data.thumbnailIndex || 0,
                mediaType: "documents",
                documentUrls: data.documentUrls || [],
                documentMetadata: data.documentMetadata || [],
              },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "update", id, collection, ...res });
        } catch {}
        resetDraft();
        return res;
      }

      // Embed posts (YouTube, SoundCloud)
      if (poster.value.embedHtml || poster.value.url) {
        // Re-detect embed if URL was changed in the input
        const currentUrl = posterInput.value?.trim() || poster.value.url || "";
        let embedHtml = poster.value.embedHtml || "";
        let embedProvider = poster.value.embedProvider || null;
        let thumbnailUrl = poster.value.thumbnailUrl || "";

        if (currentUrl && currentUrl !== poster.value.url) {
          const embed = await detectEmbed(currentUrl);
          if (embed?.html) {
            embedHtml = sanitizeEmbedHtml(embed.html);
            embedProvider = embed.provider || null;
            thumbnailUrl = embed.thumbnailUrl || "";
          }
        }

        const imageDelivery = await normalizeImageUrls({
          includeVariants: true,
        });

        // Sync imageCaptions to match images length
        syncImageCaptions();

        // For embeds the composer only exposes one caption field (bottom block).
        // Sync description = caption so resolveCardText (which prefers description)
        // always shows the latest user-entered value.
        const embedCaption = captionText;
        const embedDescription = captionText;

        const data = {
          itemName,
          caption: embedCaption, // Legacy compat
          description: embedDescription, // Keep in sync with caption for card display
          imageCaptions: [...imageCaptions.value], // Per-image captions
          ...imageDeliveryFields(imageDelivery, 0),
          owner,
          organisation,
          mediaType: "embed",
          type: "media",
          thumbnailIndex: 0,
          embedHtml,
          embedUrl: currentUrl,
          embedProvider,
          thumbnailUrl,
        };

        console.log("[PostComposer] saveEdit: embed branch", {
          itemName: data.itemName,
          embedUrl: data.embedUrl,
          embedProvider: data.embedProvider,
          mediaType: data.mediaType,
        });

        const res = await store.dispatch("mediaActions/updateMediaItem", {
          id,
          collection,
          data,
          existingUrls: data.images || [],
        });
        try {
          await store.dispatch("mediaActions/fetchAllMediaPosts");
        } catch {}
        try {
          window.dispatchEvent(
            new CustomEvent("marketplace:item-updated", {
              detail: {
                originalPath: resolvedOriginalPath,
                itemName: data.itemName,
                itemDescription: data.caption,
                description: data.description || "",
                caption: data.caption || "",
                imageCaptions: data.imageCaptions || [],
                images: data.images || [],
                mediaVariants: data.mediaVariants || [],
                variants: data.variants || {},
                thumbnailIndex: 0,
                mediaType: "embed",
                embedHtml: data.embedHtml,
                embedUrl: data.embedUrl,
                embedProvider: data.embedProvider,
                thumbnailUrl: data.thumbnailUrl,
              },
            }),
          );
        } catch {}
        try {
          emit("saved", { mode: "update", id, collection, ...res });
        } catch {}
        resetDraft();
        return res;
      }

      const imageDelivery = await normalizeImageUrls({
        includeVariants: true,
      });
      const imageUrls = imageDelivery.urls;
      const clampedThumb = imageUrls.length
        ? Math.max(
            0,
            Math.min(Number(thumbnailIndex.value) || 0, imageUrls.length - 1),
          )
        : 0;
      const mediaType = imageUrls.length
        ? "image"
        : deriveTextualMediaType(itemName, captionText);

      // Sync imageCaptions to match images length
      syncImageCaptions();

      const data = {
        itemName,
        caption: captionText, // Legacy compat
        description: description.value.trim(), // Whole-post description
        imageCaptions: [...imageCaptions.value], // Per-image captions
        ...imageDeliveryFields(imageDelivery, clampedThumb),
        owner,
        organisation,
        mediaType,
        type: "media",
        thumbnailIndex: clampedThumb,
      };
      const res = await store.dispatch("mediaActions/updateMediaItem", {
        id,
        collection,
        data,
        existingUrls: data.images || [],
      });
      try {
        await store.dispatch("mediaActions/fetchAllMediaPosts");
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("marketplace:item-updated", {
            detail: {
              originalPath: resolvedOriginalPath,
              itemName: data.itemName,
              itemDescription: data.caption,
              description: data.description || "",
              caption: data.caption || "",
              imageCaptions: data.imageCaptions || [],
              images: data.images || [],
              mediaVariants: data.mediaVariants || [],
              variants: data.variants || {},
              thumbnailIndex: data.thumbnailIndex || 0,
              mediaType: data.mediaType,
              // Clear stale video/audio URLs so the grid doesn't treat this as a video/audio post
              mediaUrl: "",
              audioUrl: "",
            },
          }),
        );
      } catch {}
      try {
        emit("saved", { mode: "update", id, collection, ...res });
      } catch {}
      resetDraft();
      return res;
    };

    // Background submit: start a global upload job and run save() asynchronously
    // so the overlay can close while publishing continues.
    const submitInBackground = ({ owner, organisation } = {}) => {
      const filename =
        title.value?.trim() || caption.value?.trim() || "Publishing post";

      // Start upload tracking (indeterminate until per-file progress is wired)
      store
        .dispatch("uploadQueue/startUpload", { filename })
        .then((uploadId) => {
          // Switch to determinate mode immediately
          store.dispatch("uploadQueue/updateProgress", {
            id: uploadId,
            progress: 0,
          });
          Promise.resolve()
            .then(() => save({ owner, organisation, uploadId }))
            .then(() =>
              store.dispatch("uploadQueue/finishUpload", { id: uploadId }),
            )
            .catch((error) => {
              store.dispatch("uploadQueue/failUpload", {
                id: uploadId,
                error: error?.message || String(error || "Upload failed"),
              });
            });
        })
        .catch((e) => {
          console.warn("[PostComposer] Failed to start upload queue:", e);
        });
    };

    expose({
      save,
      saveEdit,
      collectDraft,
      loadFromItem,
      isDirty,
      resetDraft,
      backToEdit,
      submitInBackground,
    });

    return {
      smartTitleCase,
      viewMode,
      goToPreview,
      onNext,
      onInlineSaveClick,
      onCancel,
      titleField,
      descField,
      captionField,
      imageCaptionField,
      activeField,
      title,
      caption,
      description,
      imageCaptions,
      currentImageCaption,
      isMultiImagePost,
      displayCaption,
      previewDisplayText,
      isEditingMintedNFT,
      editingItemId,
      deleteConfirmPhase,
      captionPlaceholder,
      genericPlaceholder,
      titlePlaceholder,
      showPreviewArea,
      showFooter,
      activeDocIndex,
      activeDoc,
      isActiveDocText,
      isActiveDocPdf,
      isActiveDocOffice,
      activeDocExtension,
      activeDocTextContent,
      docTextContents,
      hasMediaSelected,
      hideInlineInputRow,
      isEmojiPickerOpen,
      recentEmojis,
      toggleEmojiPicker,
      handleEmojiSelected,
      onDeletePost,
      onMintNFTClick,
      isNftMintPending,
      isMediaMenuOpen,
      toggleMediaMenu,
      captureMode,
      isMobile,
      onCameraFooterClick,
      openUploadPicker,
      openLiveCamera,
      stopCamera,
      canShowCameraIcon,
      showCameraIcon,
      canUseCamera,
      openDevicePicker,
      onCameraTileClick,
      cameraPreviewRef,
      cameraTileRef,
      onEditThumb,
      isRecording,
      recordingProgressDeg,
      currentFacingMode,
      switchCamera,
      onShutterPressStart,
      onShutterPressEnd,
      onShutterPressCancel,
      cameraError,
      onThumbDragStart,
      onThumbDrop,
      onTrayReorder,
      mediaItemsForTray,
      selectedIndexForTray,
      onTraySelect,
      removeMediaAt,
      onTrayEdit,
	      onTrayReorderMixed,
	      thumbnailSrc,
	      mediaPreviewSrc,
	      isVideoMedia,
	      currentPreviewMedia,
	      videoPreviewUrl,
	      standaloneVideoPosterSrc,
	      audioPreviewUrl,
      allInputId,
      imageInputId,
      imageLibraryInputId,
      videoInputId,
      videoLibraryInputId,
      docInputId,
      audioInputId,
      selectedType,
      images,
      previewFocused,
      thumbnailIndex,
      documents,
      videoFile,
      audioFile,
      poster,
      posterInput,
      posterUrlInputRef,
      embedError,
      removeEmbed,
      hasPreview,
      previewItem,
      presentationPreviewItem,
      usesAdaptivePresentationPreview,
      previewPresentationLabel,
      imageInput,
      imageLibraryInput,
      videoInput,
      videoLibraryInput,
      docInput,
      audioInput,
      audioCoverInput,
      audioCoverInputId,
      audioCoverImage,
      audioCoverPreviewUrl,
      onPickAudioCover,
      clearAudioCover,
      allInput,
      chooseType,
      activateMediaInput,
      onPickImages,
      onPickVideo,
      onPickDocuments,
      onPickAudio,
      onAddAudioClick,
      hasAudio,
      clearAudio,
      onPickMixedFiles,
      onPosterInput,
      onImagesUpdate,
      removeImageAt,
      onFileInputClick,
      collectDraft,
      save,

    };
  },
};
</script>

<style scoped>
/* ── Contenteditable inline fields ── */
.editable-field {
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 1.6em;
  cursor: text;
}
.editable-field:empty::before {
  content: attr(data-placeholder);
  color: var(--color-text-supporting);
  pointer-events: none;
}
.editable-field:focus {
  outline: none;
}
/* Action bar (replaces inputRow for footers without a textarea) */
.actionBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.actionIcons {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.actionIcon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inputRow {
  gap: var(--space-3);
}
.inputRow textarea {
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  outline: none;
  resize: none;
  overflow-y: hidden;
  line-height: 20px;
  max-height: calc(
    20px * 12
  ); /* Increased from 5 to 12 lines for better editing */
}
.hidden {
  display: none;
}
.gray {
  color: var(--color-text-supporting);
}
.previewArea {
  margin-top: 12px;
  /* Constrain to viewport minus Overlay header/padding overhead so content never spills */
  max-height: calc(100vh - 140px);
  overflow: hidden;
}

/* Masonry Mockup for Preview Mode - simulates MasonryMarketplace layout */
/* Each column ~300px in real masonry (900px / 3), we use smaller scale here */
.masonry-mockup-container {
  display: flex;
  justify-content: center;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--color-surface-canvas);
}
.masonry-mockup {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-3);
}
.mockup-column {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 300px; /* Match masonry column width */
  flex-shrink: 0;
}
.mockup-column-edge {
  width: 80px; /* Only show partial edge of placeholder cards */
  overflow: hidden;
}
.mockup-column-center {
  width: 300px;
}

/* Simple placeholder rectangles - just gray boxes */
.mockup-placeholder {
  background: var(--color-surface-raised);
  opacity: 0.4;
  border-radius: var(--radius-sm);
}
.mockup-placeholder-short {
  height: 120px;
}
.mockup-placeholder-medium {
  height: 200px;
}
.mockup-placeholder-tall {
  height: -webkit-fill-available;
}

/* Wrapper for the real ImageCard preview */
.preview-card-wrapper {
  width: 100%;
}
.masonry-item-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-2);
}
.masonry-item-title {
  font-size: var(--font-size-heading);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  color: var(--color-text-primary);
  /* Truncation logic usually handled in JS but basic CSS truncate for safety */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.masonry-mockup-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: var(--radius-sm);
  background: var(--color-surface-canvas);
  gap: var(--space-3);
}
.presentation-preview-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
}
.presentation-preview-heading {
  width: min(340px, 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  color: var(--color-text-primary);
  font-size: var(--font-size-label);
}
.presentation-preview-mode {
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--color-surface-raised);
  font-weight: var(--font-weight-medium);
}
.presentation-preview-phone {
  width: min(340px, 100%);
  aspect-ratio: 9 / 16;
  min-height: 0;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: #080808;
}
@media (max-height: 820px) {
  .presentation-preview-heading,
  .presentation-preview-phone {
    width: min(300px, 100%);
  }
}
@media (max-height: 700px) {
  .presentation-preview-heading,
  .presentation-preview-phone {
    width: min(250px, 100%);
  }
}
.embedContainer iframe {
  width: 100%;
  height: 315px;
}
.docList {
  margin: 8px 0 0;
  padding: 0 0 0 16px;
}
.mediaMenu {
  align-self: flex-end;
  display: flex;
  gap: 6px;
  padding: 6px;
  margin: 8px 0;
}

.media-menu__file-action {
  box-sizing: border-box;
  display: inline-flex;
  height: calc(var(--control-height-compact) * 0.85);
  align-items: center;
  justify-content: center;
  margin-bottom: 0;
  padding: var(--space-4);
  border-radius: var(--radius-pill);
  color: var(--color-text-primary);
  font-family: var(--font-family-ui);
  font-size: var(--font-size-label);
  cursor: pointer;
  transition-property: background-color, color, transform;
  transition-duration: var(--motion-standard);
  transition-timing-function: var(--motion-easing-standard);
}

.media-menu__file-action:hover {
  background: var(--color-surface-subtle);
  transform: translateY(var(--motion-pressed-offset));
}

.media-menu__file-action:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}
.card {
  background: var(--color-surface-canvas);
  border-radius: var(--radius-sm);
}
.shadow {
  box-shadow: var(--shadow-surface-recessed);
}

/* Phone preview frame: designed for 1080x1920, scaled down via CSS var */
.phoneFrame {
  --phone-scale: 0.333; /* 1080 -> 360px default */
  width: calc(1080px * var(--phone-scale));
  height: calc(1920px * var(--phone-scale));
  max-height: 100%;
  border-radius: var(--radius-sm);
  overflow: hidden;
  margin: 0 auto;
  position: relative;
}
.phoneViewport {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-height: 0; /* allow inner flex children to shrink */
  overflow-y: auto;
}
.mediaRegion {
  flex: 6; /* default when not in fill mode */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: var(--color-surface-canvas);
  border-radius: var(--radius-sm);
  flex-direction: column;
  min-height: 0; /* critical for nested flex layout */
}

.textRegion {
  flex: 4; /* default when not in fill mode */
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm);
  overflow: scroll;
}
.textRegion.fill,
.mediaRegion.fill {
  flex: 1 1 auto;
  width: 100%;
  /* important: allow flex item to shrink instead of forcing 100% height */
  min-height: 0;
  position: relative;
  /* align-items: center;
  justify-content: space-evenly; */
}

.previewTitle {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-heading);
  line-height: 1.2;
  max-height: calc(var(--font-size-heading) * 3.6);
  overflow-y: auto;
}
.previewTitleInput {
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-heading);
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text-primary);
}
.previewCaption {
  font-size: var(--font-size-body);
  line-height: 1.4;
  color: var(--color-text-primary);
}
.previewCaption.placeholder {
  color: var(--color-text-subtle);
}

/* Active editing indicator for text region fields */
.previewTitle.editing-active,
.previewCaption.editing-active {
  position: relative;
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  background: rgba(0, 123, 255, 0.05);
  box-shadow: inset 0 0 0 2px #007bff;
}
.previewTitle.editing-active::after,
.previewCaption.editing-active::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: var(--color-text-primary);
  margin-left: 2px;
  animation: blink-cursor 1s step-end infinite;
  vertical-align: text-bottom;
}

.imageUploadInFrame :deep(img) {
  border-radius: 8px;
}
.previewGhost {
  font-size: 12px;
}

.fullMedia {
  object-fit: cover;
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-media-canvas);
  display: flex;
  flex-direction: column;
}

/* When inside .videoArea, prevent the video from forcing overflow */
.videoArea > .fullMedia {
  height: auto;
  max-height: 100%;
}

.compactImg {
  width: var(--control-height-dense) !important;
  height: var(--control-height-dense) !important;
}

.mediaPreviewContainer {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.videoArea {
  position: relative;
  flex: 1 1 auto;
  min-height: calc(1080px * var(--phone-scale) * 9 / 16); /* enforce at least 16:9 */
  overflow: hidden;
  border-radius: var(--radius-sm);
  width: -webkit-fill-available;
}


/* YouTube / SoundCloud embed preview in composer */
.embedPreviewArea {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: calc(1080px * var(--phone-scale) * 9 / 16); /* enforce at least 16:9 */
  overflow: hidden;
  background-color: #000;
  border-radius: var(--radius-sm);
  flex: 1 1 auto; /* can grow when space available, won't shrink below min-height */
}

/* :deep required because iframe is injected via v-html (no scoped attr) */
.embedPreviewArea :deep(iframe) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.embed-has-error {
  opacity: 0.5;
}

.embedPreviewArea.embed-vertical {
  aspect-ratio: 9 / 16;
  max-height: 70vh;
  width: auto;
  max-width: 100%;
  margin: 0 auto;
}

/* Tiny margin between title and embed media */
.embed-title-block {
  margin-bottom: var(--space-2, 4px);
}

/* Editable embed URL input */
.embed-url-row {
  margin-top: var(--space-2, 4px);
}
.embed-url-input {
  width: 100%;
  font-size: var(--font-size-body);
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-field);
  color: var(--color-text-primary);
  -webkit-text-fill-color: var(--color-text-secondary);
  min-height: var(--control-height-micro);
  outline: none;
  box-sizing: border-box;
}
.embed-url-input:focus {
  border: 1px solid var(--color-border-subtle);
}

.embedErrorRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-body);
}

.embedErrorLabel {
  color: var(--color-feedback-danger-foreground);
  font-weight: var(--font-weight-medium);
}

.imageInfoBlock {
  color: var(--color-text-primary);
}
.imageInfoBlock.topBlock {
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  font-size: var(--font-size-heading);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
  padding: var(--space-2) var(--space-4);
  max-height: calc(var(--font-size-heading) * 3.6);
  overflow-y: auto;
}

/* Title text in top block */
.imageInfoBlock.topBlock .title-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.imageInfoBlock.bottomBlock {
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
  background: var(--color-surface-canvas);
  padding: var(--space-2);
  min-height: calc(var(--font-size-body) * 2);
  max-height: calc(var(--font-size-body) * 1.4 * 8); /* ~8 lines, then scroll */
  overflow-y: auto;
}

/* Caption text styling - matches ImageCard masonry style */
.imageInfoBlock .caption-text {
  flex: 1;
  min-width: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Placeholder text styling */
.placeholder-text {
  color: var(--color-text-subtle);
  font-style: italic;
}

/* Description block for multi-image posts (whole-post description) */
.imageInfoBlock.descriptionBlock {
  background: var(--color-surface-canvas);
  padding: var(--space-4);
  margin-top: var(--space-2);
  border-radius: var(--radius-sm);
  min-height: calc(var(--font-size-body) * 2.5);
  max-height: calc(var(--font-size-body) * 1.4 * 8); /* ~8 lines, then scroll */
  overflow-y: auto;
}
.imageInfoBlock.descriptionBlock .description-text {
  font-size: calc(var(--font-size-body) * 1.1);
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}
.imageInfoBlock.descriptionBlock.placeholder
  .description-text
  .placeholder-text {
  color: #999;
  font-style: italic;
}

/* Active editing indicator - block cursor style */
.imageInfoBlock.editing-active {
  position: relative;
  background: var(--color-surface-canvas);
  box-shadow: inset 0 0 0 2px #007bff;
}
.imageInfoBlock.editing-active::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 1em;
  background-color: var(--color-text-primary);
  margin-left: 2px;
  animation: blink-cursor 1s step-end infinite;
  vertical-align: text-bottom;
}
@keyframes blink-cursor {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.imageArea {
  position: relative;
  flex: 1 1 auto;
  min-height: calc(1080px * var(--phone-scale) * 9 / 16); /* enforce at least 16:9 */
  overflow: hidden;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm);
}
.imageArea.videoStill {
  background: #000;
}
.composerVideoOverlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
}
.composerVideoPlay {
  width: calc(var(--control-height-default, 48px) * 1.1);
  height: calc(var(--control-height-default, 48px) * 1.1);
  color: #fff;
  border-radius: 999px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.45);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
}
.docsArea {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: var(--color-surface-raised);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
}

.docListInline {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2);
}

.docPreview {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.docEmbed {
  width: 100%;
  height: 100%;
  border: none;
}
.docPlaceholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--color-text-emphasis);
  gap: 8px;
}
.docPlaceholder .iconLg {
  width: 48px;
  height: 48px;
}
.docPlaceholder .docName {
  text-align: center;
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Text file preview (.txt, .md, .csv, .json, etc.) */
.docTextPreview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: var(--radius-sm);
  overflow: hidden;
}
.docTextHeader {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-surface-raised);
  flex-shrink: 0;
}
.docTypeBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  background: var(--color-surface-raised);
  color: var(--color-text-emphasis);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}
.docTypeBadge.office {
  background: #dbeafe;
  color: #1d4ed8;
}
.docTextName {
  font-size: var(--font-size-label);
  color: var(--color-text-supporting);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.docTextBody {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: var(--space-4);
  margin: 0;
  font-family: inherit;
  font-size: var(--font-size-label);
  line-height: 1.5;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-wrap: break-word;
  tab-size: 4;
}
.docTextLoading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-subtle);
  font-size: var(--font-size-label);
}

/* Office doc placeholder (.doc, .docx, .ppt, .xls, etc.) */
.docOfficePlaceholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  background: linear-gradient(135deg, #f8fafc, #eef2ff);
  border-radius: var(--radius-sm);
  padding: var(--space-4);
}
.docOfficeIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 50%;
  padding: var(--space-4);
}
.docOfficeIcon .iconLg {
  width: 48px;
  height: 48px;
}
.docOfficePlaceholder .docName {
  text-align: center;
  max-width: 90%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}
.docOfficeHint {
  color: var(--color-text-subtle);
  font-size: var(--font-size-caption);
}

.imageAreaImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.titleOverlay {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 2;
}
.captionOverlay {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2;
}
.overlayInput {
  width: 100%;
  background: var(--color-surface-raised);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  outline: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.overlayTitle {
  font-weight: 600;
}
.overlayCaption {
  max-height: calc(20px * 5);
  line-height: 20px;
}
.deleteBtn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: var(--radius-sm);
  padding: 6px;
}

.captureOverlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 18px;
  pointer-events: none; /* only the shutter is clickable */
}
.shutterWrap {
  pointer-events: auto;
}
.shutterButton {
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.25);
  display: grid;
  place-items: center;
}
.shutterButton .dot {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ff3b30; /* iOS red */
  transition: transform 120ms ease;
}
.shutterButton.recording .dot {
  transform: scale(0.8);
  border-radius: 10px;
}
.shutterButton .ring {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: conic-gradient(#ff3b30 var(--progress, 0deg), transparent 0);
}

/* Switch camera button — top-right of capture overlay */
.switchCameraBtn {
  position: absolute;
  top: 14px;
  right: 14px;
  pointer-events: auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.18s ease, transform 0.18s ease;
  z-index: 2;
}
.switchCameraBtn:hover {
  background: rgba(0, 0, 0, 0.6);
}
.switchCameraBtn:active {
  transform: scale(0.9);
}
.switchCameraBtn:disabled {
  opacity: 0.35;
  pointer-events: none;
}

.composerOverlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 8px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.45) 40%,
    rgba(0, 0, 0, 0.6) 100%
  );
  color: #fff;
  z-index: 1; /* under input footer */
}

/* Upload tray aligned above footer for new posts */
.footerMediaTray {
  display: flex;
  align-items: center;
  padding: var(--space-3) 0;
}

/* Phone footer input row stays at the bottom of the viewport */
.phoneFooter {
  margin-top: auto;
  display: flex;
  align-items: center;
  position: sticky;
  padding: 1px;
  bottom: 0;
  z-index: 2; /* above composerOverlay */
  background: var(--color-surface-canvas);
}



/* Inline delete pill — fixed size, swaps trash icon for '?' on confirm */
.delete-action {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-pill, 25px);
  transition: background-color 0.2s ease;
  flex-shrink: 0;
}

.delete-action:hover {
  background-color: var(--color-feedback-danger-confirm-surface);
}

.delete-action:hover .compactImg {
  stroke: var(--color-feedback-danger-foreground);
}

.delete-action .delete-icon {
  width: 16px;
  height: 16px;
}

.delete-action.confirm-phase {
  background-color: var(--color-feedback-danger-solid);
}

.delete-action .delete-question {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-on-danger-solid);
  line-height: 1;
  user-select: none;
}

.nextBtn {
  margin-left: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius-pill, 8px);
  border: 1px solid var(--color-border-muted);
  background: var(--color-surface-raised);
  color: var(--color-text-primary);
}
.nextBtn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cancelBtn {
  margin-left: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius-pill, 8px);
  border: 1px solid var(--color-border-muted);
  background: var(--color-surface-canvas);
  color: var(--color-text-primary);
}
/* New inline input row buttons: Cancel text left, forward arrow right */
.cancelTextBtn {
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: 14px;
  padding: 0 6px 0 0;
}
.forwardBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--control-height-compact);
  padding: 0;
  border: none !important;
}

.post-composer-save-button {
  height: var(--control-height-compact);
  min-height: var(--control-height-compact);
  min-width: var(--control-height-compact);
  padding-inline: var(--space-4);
  font-size: var(--font-size-body);
}

/* Remove embed — plain text button, red color */
.remove-embed-btn {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label, 12px);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.remove-embed-btn:hover {
  opacity: 0.7;
}

/* NFT mint icon selected/pending state */
.nft-icon-selected {
  stroke: var(--color-accent-selection) !important;
  color: var(--color-accent-selection);
  background: var(--color-accent-surface);
  color: var(--color-text-on-accent-subtle);
  /* Debug: add visible border */
  border: 1px solid var(--color-accent-selection) !important;
  border-radius: var(--radius-sm);
  padding: 2px;
  box-sizing: content-box;
}
/* Info bars to mirror ImageCard’s .image-card__info look */
.imageInfoBar {
  position: absolute;
  left: 8px;
  right: 8px;
  background: var(--color-surface-raised);
  color: var(--color-text-primary);
  padding: var(--space-2);
  border-radius: var(--radius-sm);
  max-height: 36%;
  overflow: auto;
}
.imageInfoBar.topBar {
  top: 8px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.imageInfoBar.bottomBar {
  bottom: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.controlsRow {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}
.inlineControls {
  padding-top: var(--space-2);
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Upload placeholder - same size as thumbnails, corner frame effect */
.upload-placeholder {
  width: calc((var(--control-height-default, 48px)) * 2);
  height: calc((var(--control-height-default, 48px)) * 2);
  margin-bottom: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  border-radius: var(--radius-md);
  transition: background-color var(--motion-standard) ease;
}

/* Corner frame using CSS mask on a border - creates rounded corner brackets */
.upload-placeholder::before {
  content: "";
  position: absolute;
  inset: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  opacity: 0.5;
  pointer-events: none;
  /* Mask to show only corners - uses radial gradients at each corner */
  -webkit-mask:
    /* Top-left corner */ radial-gradient(
        circle at 0 0,
        transparent 0,
        transparent 1px,
        black 1px
      )
      0 0 / 20px 20px no-repeat,
    /* Top-right corner */
      radial-gradient(
        circle at 100% 0,
        transparent 0,
        transparent 1px,
        black 1px
      )
      100% 0 / 20px 20px no-repeat,
    /* Bottom-left corner */
      radial-gradient(
        circle at 0 100%,
        transparent 0,
        transparent 1px,
        black 1px
      )
      0 100% / 20px 20px no-repeat,
    /* Bottom-right corner */
      radial-gradient(
        circle at 100% 100%,
        transparent 0,
        transparent 1px,
        black 1px
      )
      100% 100% / 20px 20px no-repeat;
  mask: radial-gradient(
        circle at 0 0,
        transparent 0,
        transparent 1px,
        black 1px
      )
      0 0 / 20px 20px no-repeat,
    radial-gradient(circle at 100% 0, transparent 0, transparent 1px, black 1px)
      100% 0 / 20px 20px no-repeat,
    radial-gradient(circle at 0 100%, transparent 0, transparent 1px, black 1px)
      0 100% / 20px 20px no-repeat,
    radial-gradient(
        circle at 100% 100%,
        transparent 0,
        transparent 1px,
        black 1px
      )
      100% 100% / 20px 20px no-repeat;
  transition: opacity var(--motion-standard) ease;
}

.upload-placeholder:hover {
  background: var(--color-surface-highlight-faint);
}

.upload-placeholder:hover::before {
  opacity: 0.8;
}

.upload-placeholder-icon {
  width: 32px;
  height: 32px;
  opacity: 0.4;
  color: var(--color-icon-subtle);
}

.upload-placeholder-label {
  font-size: 11px;
  color: var(--color-text-subtle);
  opacity: 0.5;
  white-space: nowrap;
  pointer-events: none;
  line-height: 1;
}

.cameraTile {
  position: relative;
  width: 120px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
}
.cameraTile video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cameraBadge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 999px;
  padding: 4px;
}
.iconSm {
  width: 16px;
  height: 16px;
  fill: currentColor;
}
.uploadTile {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  white-space: nowrap;
}
.thumbsStrip {
  display: flex;
  gap: 6px;
  align-items: center;
  overflow-x: auto;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* smooth scroll on iOS */
}
.thumb {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  border: 2px solid transparent;
  flex: 0 0 auto; /* prevent shrinking so horizontal scroll works nicely */
}
.thumb.cover {
  border-color: var(--color-accent-primary);
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb .thumbDelete {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 4px;
  padding: 2px;
}

.inlineInputRow {
  display: flex;
  align-items: center;
  gap: 8px;
}
.inlineInputRow textarea {
  flex: 1;
  min-width: 0;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  resize: none;
  overflow-y: hidden;
  line-height: 20px;
  max-height: calc(
    20px * 12
  ); /* Increased from 5 to 12 lines for better editing */
}

@media (min-width: 768px) {
  .phoneFrame {
    --phone-scale: 0.4;
    margin-left: 0;
    margin-right: auto;
  } /* 540x960, left-aligned */
}

/* Audio Player Styles */
.audioPreview {
  width: 100%;
  text-align: center;
}

.audioPreview.videoContext {
  margin-top: var(--space-3);
}
.audioPreview.audioContext {
  margin-top: var(--space-5);
}
.audioBadge {
  margin: 0 auto;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-label);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.audioHeader {
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  font-size: var(--font-size-caption);
}
.audioName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: var(--space-3);
}
.removeAudioBtn {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
  flex-shrink: 0;
}
.post-composer-text-action {
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-weight: var(--font-weight-normal);
  line-height: inherit;
}
.audioControl {
  width: 100%;
  margin-top: var(--space-3);
}

/* Audio Cover Image */
.audioCoverSection {
  margin-top: var(--space-3);
  display: flex;
  justify-content: center;
}
.audioCoverThumb {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
}
.audioCoverImg {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: var(--radius-sm);
}
.removeCoverBtn {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
}
.audioCoverPicker {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-sm);
  border: 1px dashed var(--color-border-dropzone-subtle);
  color: var(--color-text-supporting);
  font-size: var(--font-size-label);
  transition: border-color 0.15s, color 0.15s;
}
.audioCoverPicker:hover {
  border-color: var(--color-border-dropzone-hover);
  color: var(--color-text-high-emphasis);
}
.audioCoverLabel {
  white-space: nowrap;
}
</style>
